import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useDrag, useDrop } from 'react-dnd';
import { api } from '../../app/http-common';
import { useCanEdit, useIsAdmin } from '../../hooks/useFrontegg';
import { useConfig } from '../../contexts/FrontendConfigContext';
import { mergeInProdItems, pendingMapsEqual } from './inProdMerge';
import { useAuthUserOrNull } from '@frontegg/react';
import { useNavigate } from 'react-router-dom';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { clipboardWriteDeferred } from '../../utils/clipboard';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FilterListIcon from '@mui/icons-material/FilterList';
import FastForwardOutlinedIcon from '@mui/icons-material/FastForwardOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AppModal from '../../components/AppModal/AppModal';
import SendSuggestionsModal from '../../components/SendSuggestionsModal/SendSuggestionsModal';
import './BucketManager.css';

const STORY_CARD_TYPE = 'BUCKET_STORY_CARD';
const BUCKET_COLUMN_TYPE = 'BUCKET_COLUMN_DRAG';
const LOCAL_STORAGE_KEY = 'alchemiq_bucket_visibility';
const LOCAL_STORAGE_ORDER_KEY = 'alchemiq_bucket_order';

const STATUS_OPTIONS = [
  'suggestion', 'accepted', 'in_work', 'completed',
  'sent_to_publisher', 'published', 'rejected', 'deleted',
];

const STATUS_COLORS = {
  suggestion: '#2196F3',
  accepted: '#9C27B0',
  in_work: '#FF9800',
  completed: '#009688',
  sent_to_publisher: '#9E9E9E',
  published: '#4CAF50',
  rejected: '#F44336',
  deleted: '#BDBDBD',
};

const STATUS_LABELS = {
  suggestion: 'Suggestion',
  accepted: 'Accepted',
  in_work: 'In Progress',
  completed: 'Completed',
  sent_to_publisher: 'Sent',
  published: 'Published',
  rejected: 'Rejected',
  deleted: 'Deleted',
};

function formatFallbackPageViews(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return '';
  }

  return `${numericValue.toLocaleString()} PV`;
}

const STATUS_FORWARD_MAP = {
  suggestion: 'accepted',
  accepted: 'in_work',
  in_work: 'completed',
  completed: 'sent_to_publisher',
  sent_to_publisher: 'published',
  published: 'suggestion',
};

const IN_PROD_STATUSES = ['accepted', 'in_work', 'completed', 'sent_to_publisher'];

const STATUS_SORT_PRIORITY = {
  suggestion: 0,
  accepted: 1,
  in_work: 2,
  completed: 3,
  sent_to_publisher: 4,
  published: 5,
  rejected: 6,
  deleted: 7,
};

// Circular forward map within In Production statuses (badge click cycles through these)
const IN_PROD_FORWARD = {
  accepted: 'in_work',
  in_work: 'completed',
  completed: 'sent_to_publisher',
  sent_to_publisher: 'accepted',
};

const formatIsraelTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString('en-GB', {
      timeZone: 'Asia/Jerusalem',
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

// ── Helpers ───────────────────────────────────────────────────

function loadVisibility() {
  try { return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || {}; }
  catch { return {}; }
}
function saveVisibility(map) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(map));
}
function loadBucketOrder() {
  try { return JSON.parse(localStorage.getItem(LOCAL_STORAGE_ORDER_KEY)) || []; }
  catch { return []; }
}
function saveBucketOrder(order) {
  localStorage.setItem(LOCAL_STORAGE_ORDER_KEY, JSON.stringify(order));
}

// ── Status groups for reorder restriction ─────────────────────
function getStatusGroup(status) {
  if (status === 'suggestion') return 'suggestion';
  if (status === 'accepted') return 'accepted';
  return 'other'; // in_work, completed, sent_to_publisher
}

// ── Story card (draggable + reorder drop target) ──────────────

const StoryCard = ({ item, bucketId, index, onReorder, onStatusChange, onAdvanceStatus, onRemove, onCopyTo, onMoveTo, allBuckets, droppedId, isSelected, onToggleSelect, onArchive, onUnarchive, onOpenAssign, currentUserId }) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: STORY_CARD_TYPE,
    item: () => ({ bucketStoryId: item._id, storyId: item.storyId, sourceBucketId: bucketId, index, status: item.status }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [, drop] = useDrop({
    accept: STORY_CARD_TYPE,
    hover: (dragged) => {
      if (!ref.current) return;
      if (dragged.sourceBucketId !== bucketId) return;
      if (dragged.index === index) return;
      // Only allow reorder within the same status group
      if (getStatusGroup(dragged.status) !== getStatusGroup(item.status)) return;
      onReorder(dragged.index, index);
      dragged.index = index;
    },
  });

  drag(drop(ref));

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const actionBtnRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (actionBtnRef.current && actionBtnRef.current.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const openMenu = (e) => {
    e.stopPropagation();
    if (menuOpen) { setMenuOpen(false); return; }
    if (actionBtnRef.current) {
      const rect = actionBtnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 2, left: Math.max(8, rect.right - 180) });
    }
    setMenuOpen(true);
  };

  const title = item.story?.title || `Story ${item.storyId?.toString().slice(-6)}`;
  const articles = item.articleCountSnapshot || item.story?.articleCount || 0;
  const isDropped = droppedId === item._id;
  const nextStatus = STATUS_FORWARD_MAP[item.status];

  return (
    <div
      ref={ref}
      className={`bucket-story-card ${isDragging ? 'dragging' : ''} ${isDropped ? 'just-dropped' : ''} ${isSelected ? 'bucket-story-selected' : ''} ${item.isArchived ? 'bucket-story-archived' : ''}`}
    >
      <input
        type="checkbox"
        className="bucket-story-checkbox"
        checked={isSelected}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(item._id, { shiftKey: e.shiftKey, index });
        }}
        onChange={() => {}}
      />
      <span className="bucket-story-drag-handle"><DragIndicatorIcon style={{ fontSize: 16 }} /></span>
      <div className="bucket-story-info">
        <div className="bucket-story-title">
          <span className="bucket-story-num">{item.suggestionNumber ? `#${item.suggestionNumber}` : ''}</span>
          {item.story?.slug ? (
            <a href={`/story/${item.story.slug}`} target="_blank" rel="noopener noreferrer" className="bucket-story-title-link" onClick={e => e.stopPropagation()}>{title}</a>
          ) : title}
        </div>
        <div className="bucket-story-meta">
          <span className="bucket-story-articles">{articles} articles</span>
          {formatFallbackPageViews(item.latestPageViews) ? <span className="bucket-story-analytics">{formatFallbackPageViews(item.latestPageViews)}</span> : null}
          <span
            className="bucket-story-status-text"
            style={{ color: STATUS_COLORS[item.status] || '#ccc', cursor: nextStatus ? 'pointer' : 'default', fontWeight: 500 }}
            title={nextStatus ? `Click to advance to ${STATUS_LABELS[nextStatus]}` : item.status}
            onClick={(e) => { e.stopPropagation(); if (nextStatus) onAdvanceStatus(item._id); }}
          >
            {STATUS_LABELS[item.status] || item.status}
          </span>
          <span className="bucket-story-timestamp">{formatIsraelTime(item.createdAt)}</span>
          {item.clientFeedbackStatus && (
            // A bare 14px icon with only a tooltip was too easy to miss and gave
            // no clue what it meant. Labelled pill using the documented status
            // palette (CLAUDE.md → MyBriefs CMS-status colours).
            <span
              className={`bucket-story-feedback-badge bucket-story-feedback-${item.clientFeedbackStatus}`}
              title={item.clientFeedbackComment
                ? `Client ${item.clientFeedbackStatus}: ${item.clientFeedbackComment}`
                : `Client ${item.clientFeedbackStatus}`}
            >
              {item.clientFeedbackStatus === 'accepted'
                ? <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
                : <CancelOutlinedIcon style={{ fontSize: 13 }} />}
              <span>{item.clientFeedbackStatus === 'accepted' ? 'Client accepted' : 'Client rejected'}</span>
              {item.clientFeedbackComment && (
                <ChatBubbleOutlineIcon style={{ fontSize: 12 }} />
              )}
            </span>
          )}
          {item.assignedAdminUserId && (
            <span className="bucket-story-assignee" title={`Assigned: ${item.assignedAdminUserId}`}><PersonOutlinedIcon style={{ fontSize: 14 }} /></span>
          )}
        </div>
        {item.publicationUrl ? (
          <div className="bucket-story-link-row">
            <a href={item.publicationUrl} target="_blank" rel="noopener noreferrer" className="bucket-story-published-link" onClick={e => e.stopPropagation()}>
              Published URL
            </a>
          </div>
        ) : null}
      </div>
      <div className="bucket-story-card-actions">
        <button
          ref={actionBtnRef}
          className="bucket-card-action-btn"
          title="Actions"
          onClick={openMenu}
        >
          <MoreHorizIcon style={{ fontSize: 16 }} />
        </button>
        <button
          className="bucket-card-action-btn danger"
          title="Remove from bucket"
          onClick={(e) => { e.stopPropagation(); onRemove(item, bucketId); }}
        >
          <CloseIcon style={{ fontSize: 14 }} />
        </button>
        {menuOpen && menuPos && ReactDOM.createPortal(
          <div className="bucket-status-menu-portal" style={{ top: menuPos.top, left: menuPos.left }} ref={menuRef}>
            <div style={{ padding: '4px 12px', fontSize: 11, color: '#999' }}>Change Status</div>
            {STATUS_OPTIONS.map(s => (
              <button
                key={s}
                className="bucket-status-menu-item"
                disabled={s === item.status}
                onClick={() => { onStatusChange(item._id, s); setMenuOpen(false); }}
              >
                <span className="status-dot" style={{ backgroundColor: STATUS_COLORS[s] }} />
                {STATUS_LABELS[s]}
              </button>
            ))}
            <div className="bucket-status-menu-divider" />
            <div style={{ padding: '4px 12px', fontSize: 11, color: '#999' }}>Copy to</div>
            {allBuckets
              .filter(b => b._id !== bucketId)
              .map(b => (
                <button
                  key={`copy-${b._id}`}
                  className="bucket-status-menu-item"
                  onClick={() => { onCopyTo(item, b._id); setMenuOpen(false); }}
                >
                  <ContentCopyOutlinedIcon style={{ fontSize: 14 }} /> {b.name}
                </button>
              ))}
            <div className="bucket-status-menu-divider" />
            <div style={{ padding: '4px 12px', fontSize: 11, color: '#999' }}>Move to (removes from here)</div>
            {allBuckets
              .filter(b => b._id !== bucketId)
              .map(b => (
                <button
                  key={`move-${b._id}`}
                  className="bucket-status-menu-item"
                  onClick={() => { onMoveTo(item, b._id, bucketId); setMenuOpen(false); }}
                >
                  <ArrowForwardIcon style={{ fontSize: 14 }} /> {b.name}
                </button>
              ))}
            <div className="bucket-status-menu-divider" />
            <button
              className="bucket-status-menu-item"
              style={{ color: '#d32f2f' }}
              onClick={() => { onRemove(item, bucketId); setMenuOpen(false); }}
            >
              <DeleteOutlineIcon style={{ fontSize: 14 }} /> Remove from bucket
            </button>
            <div className="bucket-status-menu-divider" />
            {item.isArchived ? (
              <button
                className="bucket-status-menu-item"
                onClick={() => { onUnarchive(item._id, bucketId); setMenuOpen(false); }}
              >
                <UnarchiveOutlinedIcon style={{ fontSize: 14 }} /> Unarchive
              </button>
            ) : (
              <button
                className="bucket-status-menu-item"
                onClick={() => { onArchive(item._id, bucketId); setMenuOpen(false); }}
              >
                <ArchiveOutlinedIcon style={{ fontSize: 14 }} /> Archive
              </button>
            )}
            <div className="bucket-status-menu-divider" />
            <button
              className="bucket-status-menu-item"
              onClick={() => { onOpenAssign(item); setMenuOpen(false); }}
            >
              <PersonOutlinedIcon style={{ fontSize: 14 }} /> {item.assignedAdminUserId ? 'Reassign…' : 'Assign…'}
            </button>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

// ── Bucket column (drop target for cross-bucket moves) ────────

const BucketColumn = ({
  bucket, stories, allBuckets, onDropFromOther, onReorder,
  onStatusChange, onAdvanceStatus, onRemove, onCopyTo, onMoveTo,
  statusFilter, onStatusFilterChange, statusCounts, statusCountsAnyArchive, droppedId, isExpanded, isWorkingBucket, onToggleExpand,
  selectedIds, onToggleSelect, onSelectAll, onBulkStatusChange, searchQuery, onSearchChange,
  archiveFilter, onArchiveFilterChange, onArchive, onUnarchive,
  onShare, sharing, onCopyPublicLink, onSendToPublisher, onOpenAssign, onBulkArchive, onBulkDelete, currentUserId,
}) => {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const hasActiveFilters = Boolean(statusFilter) || Boolean(searchQuery) || (archiveFilter && archiveFilter !== 'hide');
  const showFilters = isExpanded && (filtersExpanded || hasActiveFilters);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: STORY_CARD_TYPE,
    canDrop: (dragged) => dragged.sourceBucketId !== bucket._id,
    drop: (dragged) => {
      if (dragged.sourceBucketId === bucket._id) return;
      onDropFromOther(dragged, bucket._id);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // Statuses that exist in the bucket but are entirely archived, so the archive
  // filter — not their absence — is why they have no button.
  const archivedOnlyStatuses = useMemo(() => {
    if (!statusCountsAnyArchive || archiveFilter === 'include' || archiveFilter === 'only') return [];
    return STATUS_OPTIONS
      .filter(s => s !== 'deleted')
      .map(s => [s, statusCountsAnyArchive[s] || 0])
      .filter(([s, total]) => total > 0 && (statusCounts?.[s] || 0) === 0);
  }, [statusCountsAnyArchive, statusCounts, archiveFilter]);

  const filtered = useMemo(() => {
    // The server already applied the status filter; re-filtering here is just a
    // no-op safety net for the brief window before the refetch resolves.
    const base = statusFilter
      ? stories.filter(s => s.status === statusFilter)
      : stories;
    // Group by status priority (suggestions on top), preserve original order within each group.
    return base
      .map((s, i) => ({ s, i }))
      .sort((a, b) => {
        const pa = STATUS_SORT_PRIORITY[a.s.status] ?? 99;
        const pb = STATUS_SORT_PRIORITY[b.s.status] ?? 99;
        if (pa !== pb) return pa - pb;
        return a.i - b.i;
      })
      .map(x => x.s);
  }, [stories, statusFilter]);
  const displayedStoryIds = filtered.map((item) => item._id);
  const displayedSelectedCount = displayedStoryIds.filter((storyId) => selectedIds?.has(storyId)).length;

  return (
    <div className={`bucket-column ${isOver && canDrop ? 'drag-over-column' : ''} ${!isExpanded ? 'bucket-column-collapsed' : ''} ${isWorkingBucket ? 'bucket-column-working' : ''}`}>
      <div className="bucket-column-header" onClick={onToggleExpand} style={isWorkingBucket ? { cursor: 'default' } : undefined}>
        <div className="bucket-column-title">
          <span className="bucket-expand-arrow">{isWorkingBucket ? <PushPinOutlinedIcon style={{ fontSize: 16 }} /> : (isExpanded ? <ExpandMoreIcon style={{ fontSize: 18 }} /> : <ChevronRightIcon style={{ fontSize: 18 }} />)}</span>
          <span className="bucket-col-name">{bucket.name}</span>
          <span className="bucket-col-count">{stories.length}</span>
          {bucket.targetLanguage && (
            <span className="bucket-col-lang">{bucket.targetLanguage}</span>
          )}
        </div>
        {isExpanded && (
        <div className="bucket-column-actions" onClick={e => e.stopPropagation()}>
          <button
            className={`bucket-col-btn ${sharing ? 'exporting' : ''}`}
            onClick={() => onShare(bucket._id)}
            title="Create snapshot & copy suggestion list to clipboard"
          >
            {sharing ? <HourglassEmptyIcon style={{ fontSize: 14 }} /> : <ContentCopyOutlinedIcon style={{ fontSize: 14 }} />} Copy list
          </button>
          <button
            className={`bucket-col-btn ${sharing ? 'exporting' : ''}`}
            onClick={() => onCopyPublicLink(bucket._id)}
            title="Create snapshot & copy public link"
          >
            {sharing ? <HourglassEmptyIcon style={{ fontSize: 14 }} /> : <LinkOutlinedIcon style={{ fontSize: 14 }} />} Copy link
          </button>
          <button
            className={`bucket-col-btn ${sharing ? 'exporting' : ''}`}
            onClick={() => onSendToPublisher(bucket._id)}
            title="Create snapshot & send link to publisher"
          >
            {sharing ? <HourglassEmptyIcon style={{ fontSize: 14 }} /> : <SendOutlinedIcon style={{ fontSize: 14 }} />} Send link
          </button>
        </div>
        )}
      </div>
      {isExpanded && (
        <div className="bucket-filters-toggle-bar">
          <button
            className="bucket-filters-toggle-btn"
            onClick={() => setFiltersExpanded(v => !v)}
            title={showFilters ? 'Hide search & filters' : 'Show search & filters'}
          >
            {showFilters ? <ExpandMoreIcon style={{ fontSize: 16 }} /> : <ChevronRightIcon style={{ fontSize: 16 }} />}
            <FilterListIcon style={{ fontSize: 14 }} />
            <span>Search & filters</span>
            {hasActiveFilters && <span className="bucket-filters-active-dot" title="Filters active" />}
          </button>
        </div>
      )}
      {showFilters && <div className="bucket-filter-bar">
        <button
          className={`bucket-filter-btn ${!statusFilter ? 'active' : ''}`}
          onClick={() => onStatusFilterChange(bucket._id, null)}
        >
          All
        </button>
        {STATUS_OPTIONS.filter(s => s !== 'deleted').map(s => {
          // Server-side counts across the whole bucket. Falling back to the
          // loaded page only when counts have not arrived yet.
          const count = statusCounts
            ? (statusCounts[s] || 0)
            : stories.filter(st => st.status === s).length;
          if (count === 0) return null;
          return (
            <button
              key={s}
              className={`bucket-filter-btn ${statusFilter === s ? 'active' : ''}`}
              onClick={() => onStatusFilterChange(bucket._id, s)}
              style={statusFilter === s ? { backgroundColor: STATUS_COLORS[s], borderColor: STATUS_COLORS[s] } : {}}
            >
              {STATUS_LABELS[s]} ({count})
            </button>
          );
        })}
      </div>}
      {showFilters && archivedOnlyStatuses.length > 0 && (
        <div className="bucket-filter-archived-hint">
          {archivedOnlyStatuses.map(([st, n]) => `${n} ${STATUS_LABELS[st].toLowerCase()}`).join(', ')}
          {' '}only exist as archived.{' '}
          <button
            type="button"
            className="bucket-filter-archived-hint-btn"
            onClick={() => onArchiveFilterChange(bucket._id, 'include')}
          >
            Include archived
          </button>
          {' '}to see them.
        </div>
      )}
      {showFilters && (
        <div className="bucket-column-search">
          <input
            type="text"
            placeholder="Search stories..."
            value={searchQuery || ''}
            onChange={e => onSearchChange(bucket._id, e.target.value)}
          />
        </div>
      )}
      {showFilters && (
        <div className="bucket-archive-filter">
          <select
            value={archiveFilter || 'hide'}
            onChange={e => onArchiveFilterChange(bucket._id, e.target.value)}
          >
            <option value="hide">Hide archived</option>
            <option value="include">Include archived</option>
            <option value="only">Only archived</option>
          </select>
        </div>
      )}
      {isExpanded && (
        <div className="bucket-bulk-bar">
          <label className="bucket-select-all" title="Select all / deselect all">
            <input
              type="checkbox"
              checked={filtered.length > 0 && displayedSelectedCount === filtered.length}
              onChange={() => onSelectAll(bucket._id, filtered)}
            />
            {displayedSelectedCount > 0 ? `${displayedSelectedCount} selected` : 'Select all'}
          </label>
          {displayedSelectedCount > 0 && (
            <>
              {STATUS_OPTIONS.filter(s => s !== 'deleted').map(s => (
                <button
                  key={s}
                  className="bucket-bulk-btn"
                  style={{ borderColor: STATUS_COLORS[s] }}
                  onClick={() => onBulkStatusChange(bucket._id, s, filtered)}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
              <button
                className="bucket-bulk-btn"
                style={{ borderColor: '#78909c' }}
                onClick={() => onBulkArchive(bucket._id, filtered)}
              >
                <ArchiveOutlinedIcon style={{ fontSize: 14 }} /> Archive
              </button>
              <button
                className="bucket-bulk-btn"
                style={{ borderColor: '#f44336', color: '#d32f2f' }}
                onClick={() => onBulkDelete(bucket._id, filtered)}
                title="Remove selected stories from bucket"
              >
                <DeleteOutlineIcon style={{ fontSize: 14 }} /> Delete
              </button>
            </>
          )}
        </div>
      )}
      <div ref={drop} className={`bucket-column-stories ${isOver && canDrop ? 'drag-over-list' : ''} ${!isExpanded ? 'bucket-column-stories-collapsed' : ''}`}>
        {!isExpanded ? (
          <div className="bucket-column-collapsed-hint">
            {isOver && canDrop ? 'Drop here to copy' : 'Click header to expand'}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bucket-column-empty">
            {statusFilter ? 'No stories with this status' : searchQuery ? 'No matching stories' : 'Drop stories here'}
          </div>
        ) : (
          filtered.map((item, i) => (
            <StoryCard
              key={item._id}
              item={item}
              bucketId={bucket._id}
              index={i}
              onReorder={(from, to) => {
                // `from`/`to` are indexes in the visible (filtered+sorted) list.
                // Translate them back to indexes in the raw `stories` array so
                // handleReorder splices the correct items.
                const fromId = filtered[from]?._id;
                const toId = filtered[to]?._id;
                if (!fromId || !toId) return;
                const rawFrom = stories.findIndex(s => s._id === fromId);
                const rawTo = stories.findIndex(s => s._id === toId);
                if (rawFrom < 0 || rawTo < 0) return;
                onReorder(bucket._id, rawFrom, rawTo);
              }}
              onStatusChange={onStatusChange}
              onAdvanceStatus={onAdvanceStatus}
              onRemove={onRemove}
              onCopyTo={onCopyTo}
              onMoveTo={onMoveTo}
              allBuckets={allBuckets}
              droppedId={droppedId}
              isSelected={selectedIds?.has(item._id)}
              onToggleSelect={(storyId, meta) => onToggleSelect(storyId, { ...(meta || {}), visibleIds: displayedStoryIds })}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              onOpenAssign={onOpenAssign}
              currentUserId={currentUserId}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ── Droppable + draggable open slot — can receive AND be dragged to swap ──

const DroppableOpenSlot = ({ slotIdx, currentBucketId, onSwap, className, children }) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: BUCKET_COLUMN_TYPE,
    item: { bucketId: currentBucketId, fromOpenSlot: slotIdx },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: BUCKET_COLUMN_TYPE,
    drop: (dragged) => {
      if (dragged.bucketId === currentBucketId) return;
      onSwap(slotIdx, dragged.bucketId);
    },
    canDrop: (dragged) => dragged.bucketId !== currentBucketId,
    collect: (monitor) => ({ isOver: monitor.isOver() && monitor.canDrop() }),
  });

  drag(drop(ref));

  return (
    <div ref={ref} className={`${className} ${isOver ? 'bucket-open-slot-hover' : ''} ${isDragging ? 'bucket-open-slot-dragging' : ''}`}>
      {children}
    </div>
  );
};

// ── Draggable bucket wrapper for reordering buckets ───────────

const DraggableBucketColumn = ({ bucket, index, minIndex, onBucketReorder, onSwapFromOpen, isExpanded, children }) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: BUCKET_COLUMN_TYPE,
    item: { index, bucketId: bucket._id },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: BUCKET_COLUMN_TYPE,
    hover: (dragged) => {
      // Only reorder between closed buckets (items without fromOpenSlot)
      if (dragged.fromOpenSlot !== undefined) return;
      if (dragged.index === index) return;
      if (index < minIndex || dragged.index < minIndex) return;
      onBucketReorder(dragged.index, index);
      dragged.index = index;
    },
    drop: (dragged) => {
      // Accept drops from open-slot buckets → swap into that slot
      if (dragged.fromOpenSlot !== undefined) {
        onSwapFromOpen(dragged.fromOpenSlot, bucket._id);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`bucket-column-wrapper ${isDragging ? 'bucket-col-dragging' : ''} ${isOver ? 'bucket-col-hover' : ''} ${!isExpanded ? 'bucket-col-wrapper-collapsed' : ''}`}
    >
      {children}
    </div>
  );
};

// ── In Production actions dropdown ────────────────────────────

const InProdActionsMenu = ({ item, onStatusChange, onAdvance, onMove, onCopy, onArchive, onRemove, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);

  // Notify parent so it can mark this row as "active" and freeze live-sync
  // for it while the menu is open.
  useEffect(() => {
    if (typeof onOpenChange === 'function') onOpenChange(item._id, open);
    return () => {
      if (open && typeof onOpenChange === 'function') onOpenChange(item._id, false);
    };
  }, [open, item._id, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="bucket-col-btn"
        style={{ padding: '3px 10px', fontSize: '0.75rem' }}
        onClick={() => setOpen(o => !o)}
      >
        Actions <ExpandMoreIcon style={{ fontSize: 12 }} />
      </button>
      {open && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="in-prod-actions-menu"
          style={{
            position: 'fixed',
            top: ref.current?.getBoundingClientRect().bottom + 2,
            right: window.innerWidth - ref.current?.getBoundingClientRect().right,
            zIndex: 9999,
          }}
        >
          <div style={{ fontSize: '0.7rem', color: '#999', padding: '4px 12px', textTransform: 'uppercase' }}>Change Status</div>
          {STATUS_OPTIONS.map(s => (
            <button
              key={s}
              className="in-prod-actions-item"
              disabled={s === item.status}
              onClick={() => { onStatusChange(item._id, s); setOpen(false); }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_COLORS[s], display: 'inline-block', marginRight: 6 }} />
              {STATUS_LABELS[s]}
            </button>
          ))}
          <div style={{ height: 1, background: '#eee', margin: '4px 0' }} />
          {!item.isArchived && (
            <button className="in-prod-actions-item" onClick={() => { onAdvance(item._id); setOpen(false); }}>
              <FastForwardOutlinedIcon style={{ fontSize: 13 }} /> Advance Status
            </button>
          )}
          <button className="in-prod-actions-item" onClick={() => { onMove(item); setOpen(false); }}>
            <ArrowForwardIcon style={{ fontSize: 13 }} /> Move to…
          </button>
          <button className="in-prod-actions-item" onClick={() => { onCopy(item); setOpen(false); }}>
            <ContentCopyOutlinedIcon style={{ fontSize: 13 }} /> Copy to…
          </button>
          <div style={{ height: 1, background: '#eee', margin: '4px 0' }} />
          <button className="in-prod-actions-item" onClick={() => { onArchive(item._id, item.isArchived); setOpen(false); }}>
            {item.isArchived
              ? <><UnarchiveOutlinedIcon style={{ fontSize: 13 }} /> Unarchive</>
              : <><ArchiveOutlinedIcon style={{ fontSize: 13 }} /> Archive</>}
          </button>
          <button className="in-prod-actions-item" style={{ color: '#f44336' }} onClick={() => { onRemove(item._id); setOpen(false); }}>
            <DeleteOutlineIcon style={{ fontSize: 13 }} /> Remove
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Move confirmation modal ───────────────────────────────────

const MoveModal = ({ show, item, sourceBucket, destBucket, onConfirm, onCancel, inProdMode, copyMode, allBuckets, onSetDestBucket }) => {
  const [sourceMarkStatus, setSourceMarkStatus] = useState('rejected');
  const [selectedDest, setSelectedDest] = useState('');
  if (!show || !item) return null;

  const isPublished = item.status === 'published';
  const sourceBucketId = item.publisherBucketId || item.bucketId?._id || item.bucketId;
  const availableBuckets = inProdMode ? (allBuckets || []).filter(b => b._id !== sourceBucketId) : [];
  const storyTitle = item.story?.title || item.title || `Story ${item.storyId?.toString().slice(-6)}`;

  const handleDestChange = (destId) => {
    setSelectedDest(destId);
    if (onSetDestBucket) onSetDestBucket(destId);
  };

  const handleConfirm = () => {
    if (copyMode) {
      onConfirm(null);
    } else {
      onConfirm(sourceMarkStatus);
    }
  };

  return (
    <AppModal
      isOpen={show}
      title={storyTitle}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      confirmLabel="Continue"
      confirmDisabled={inProdMode && !selectedDest}
    >
      <p>
        {inProdMode
          ? `${copyMode ? 'Copy' : 'Move'} this story from ${sourceBucket} to another bucket.`
          : `This story will be ${copyMode ? 'copied' : 'moved'} from ${sourceBucket} to ${destBucket}.`}
      </p>

      {inProdMode ? (
        <div className="bucket-modal-field-group">
          <h4 className="bucket-modal-field-label">Destination Bucket</h4>
          <select
            className="bucket-modal-select"
            aria-label="Destination Bucket"
            value={selectedDest}
            onChange={(e) => handleDestChange(e.target.value)}
          >
            <option value="">Select destination bucket...</option>
            {availableBuckets.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
        </div>
      ) : null}

      {isPublished && !copyMode ? (
        <div className="bucket-modal-warning">
          <WarningAmberIcon style={{ fontSize: 16, verticalAlign: 'middle' }} /> This story is marked as published. Are you sure you want to move it?
        </div>
      ) : null}

      {!copyMode ? (
        <div className="bucket-modal-field-group">
          <h4 className="bucket-modal-field-label">Source Bucket Status</h4>
          <div className="bucket-modal-radio-group">
            <label className="bucket-modal-radio">
              <input
                type="radio"
                name="sourceStatus"
                value="rejected"
                checked={sourceMarkStatus === 'rejected'}
                onChange={() => setSourceMarkStatus('rejected')}
              />
              <span>Move and mark rejected</span>
            </label>
            <label className="bucket-modal-radio">
              <input
                type="radio"
                name="sourceStatus"
                value="deleted"
                checked={sourceMarkStatus === 'deleted'}
                onChange={() => setSourceMarkStatus('deleted')}
              />
              <span>Move and mark deleted</span>
            </label>
          </div>
        </div>
      ) : null}
    </AppModal>
  );
};

// ── Remove confirmation modal ─────────────────────────────────

const RemoveModal = ({ show, item, bucketName, onConfirm, onCancel }) => {
  if (!show || !item) return null;
  const title = item.story?.title || `Story ${item.storyId?.toString().slice(-6)}`;
  return (
    <AppModal
      isOpen={show}
      title="Remove Story"
      onCancel={onCancel}
      onConfirm={onConfirm}
      confirmLabel="Remove"
      confirmVariant="danger"
    >
      <p>
        Are you sure you want to remove <strong>"{title}"</strong> from <strong>{bucketName}</strong>?
      </p>
      <p style={{ color: '#888', fontStyle: 'italic', fontSize: '13px' }}>
        This will remove the story from the bucket entirely. It won't delete the story itself.
      </p>
    </AppModal>
  );
};

// ── Toast component ───────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return <div className={`bucket-toast ${type}`}>{message}</div>;
};

// ── Left sidebar: bucket visibility filter ────────────────────

const BucketFilterSidebar = ({ buckets, visibilityMap, onToggle, sidebarOpen, onSidebarToggle }) => {
  return (
    <>
      <button
        className={`bucket-sidebar-filter-toggle ${sidebarOpen ? 'open' : ''}`}
        onClick={onSidebarToggle}
        title={sidebarOpen ? 'Hide filter' : 'Filter buckets'}
      >
        {sidebarOpen ? <ChevronLeftIcon style={{ fontSize: 16 }} /> : <FilterListIcon style={{ fontSize: 16 }} />} Filter
      </button>
      {sidebarOpen && (
        <div className="bucket-filter-sidebar">
          <div className="bucket-filter-sidebar-header">Show / Hide Buckets</div>
          {buckets.map(b => {
            const visible = visibilityMap[b._id] !== false;
            return (
              <label key={b._id} className="bucket-filter-sidebar-item">
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={() => onToggle(b._id)}
                />
                <span className={visible ? '' : 'dimmed'}>{b.name}</span>
                {b.targetLanguage && <span className="filter-lang">{b.targetLanguage}</span>}
              </label>
            );
          })}
        </div>
      )}
    </>
  );
};

// ── Main BucketManager ────────────────────────────────────────

const BucketManager = () => {
  const canEdit = useCanEdit();
  const isAdmin = useIsAdmin();
  const user = useAuthUserOrNull();
  const navigate = useNavigate();
  const config = useConfig();
  const liveSyncIntervalMs = (() => {
    const raw = Number(config?.PUBLISHER_BUCKETS_LIVE_SYNC_INTERVAL_MS);
    return Number.isFinite(raw) && raw > 0 ? raw : 15000;
  })();

  const [buckets, setBuckets] = useState([]);
  const [bucketStories, setBucketStories] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Move modal
  const [moveModal, setMoveModal] = useState(null);

  // Remove modal
  const [removeModal, setRemoveModal] = useState(null);

  // Per-bucket status filters
  const [statusFilters, setStatusFilters] = useState({});
  const [bucketStatusCounts, setBucketStatusCounts] = useState({});
  const [bucketStatusCountsAnyArchive, setBucketStatusCountsAnyArchive] = useState({});
  // Read inside loadBucketStories without making it depend on statusFilters
  // (which would recreate the callback and retrigger loaders).
  const statusFiltersRef = useRef({});

  // Export loading per bucket


  // Share (snapshot creation) loading per bucket
  const [sharingBucket, setSharingBucket] = useState(null);

  // Missing contact point modal
  // The bucket whose Send link confirmation is open, by id.
  const [sendModalBucket, setSendModalBucket] = useState(null);

  // Flash animation for dropped items
  const [droppedId, setDroppedId] = useState(null);

  // Bucket visibility (localStorage-persisted)
  const [visibilityMap, setVisibilityMap] = useState(() => loadVisibility());

  // Bucket order (localStorage-persisted)
  const [bucketOrder, setBucketOrder] = useState(() => loadBucketOrder());

  // Left sidebar open/closed
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Open bucket slots: ordered array of max 3 bucket IDs
  // openSlots[0] = working bucket (stable), [1],[2] = FIFO side slots
  const [openSlots, setOpenSlots] = useState([]);

  // Per-bucket selected story IDs (Map<bucketId, Set<storyId>>)
  const [selectedMap, setSelectedMap] = useState({});

  // Per-bucket search queries
  const [searchQueries, setSearchQueries] = useState({});
  const searchTimeoutRef = useRef({});

  // Per-bucket archive filter: 'hide' | 'include' | 'only'
  const [archiveFilters, setArchiveFilters] = useState({});

  // Shift key tracking for shift+drag = move
  const shiftKeyRef = useRef(false);
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Shift') shiftKeyRef.current = true; };
    const onKeyUp = (e) => { if (e.key === 'Shift') shiftKeyRef.current = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp); };
  }, []);

  // ── Assignable users (editors + admins) ────────────────────
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [assignModal, setAssignModal] = useState(null); // { bucketStoryId, currentAssignee, storyTitle }
  const [assignSearch, setAssignSearch] = useState('');
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);

  // ── View toggle: board vs in-production vs published ───────
  const [activeView, setActiveView] = useState('board'); // 'board' | 'inProduction' | 'published'

  // ── In Production state ────────────────────────────────────
  const [inProdItems, setInProdItems] = useState([]);
  const [inProdTotal, setInProdTotal] = useState(0);
  const [inProdPage, setInProdPage] = useState(1);
  const [inProdStatusCounts, setInProdStatusCounts] = useState({});
  const [inProdStatusFilter, setInProdStatusFilter] = useState(null);
  const [loadingInProd, setLoadingInProd] = useState(false);

  // ── Published state ────────────────────────────────────────
  const [publishedItems, setPublishedItems] = useState([]);
  const [publishedTotal, setPublishedTotal] = useState(0);
  const [publishedPage, setPublishedPage] = useState(1);
  const [loadingPublished, setLoadingPublished] = useState(false);

  // ── Recommended state ─────────────────────────────────────
  const [recBucketId, setRecBucketId] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [recGenerationKey, setRecGenerationKey] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [refreshingRecs, setRefreshingRecs] = useState(false);
  const [recSelected, setRecSelected] = useState(new Set());
  const [recAdding, setRecAdding] = useState(null);
  const [recBulkAdding, setRecBulkAdding] = useState(false);

  const ELIGIBLE_ROLES = ['Admin', 'AlchemiqEditor'];

  const loadAssignableUsers = useCallback(async () => {
    if (usersLoaded) return;
    try {
      const data = await api.getAssignableUsers();
      const users = data.users || [];
      const eligible = users.filter(u => {
        const roles = u.fronteggRoles || [];
        return roles.some(r => ELIGIBLE_ROLES.includes(r.key || r.name));
      });
      setAssignableUsers(eligible);
      setUsersLoaded(true);
    } catch (err) {
      console.error('Failed to load assignable users:', err);
    }
  }, [usersLoaded]);

  // ── Load data ──────────────────────────────────────────────

  const loadBuckets = useCallback(async () => {
    try {
      const data = await api.getPublisherBuckets();
      setBuckets(data.buckets || []);
      return data.buckets || [];
    } catch (err) {
      console.error('Failed to load buckets:', err);
      setToast({ message: 'Failed to load buckets', type: 'error' });
      return [];
    }
  }, []);

  const loadBucketStories = useCallback(async (bucketId, query, archiveFilter, status) => {
    try {
      const params = { limit: 200, sort: 'sort_order', query: query || undefined };
      const af = archiveFilter || archiveFilters[bucketId] || 'hide';
      if (af !== 'hide') params.include_archived = af;
      // The status filter must go to the SERVER. The list sorts suggestions
      // first, so a bucket with a full page of them pushed every other status
      // past the limit and client-side filtering could never surface them.
      const st = status !== undefined ? status : statusFiltersRef.current[bucketId];
      if (st) params.status = st;
      const data = await api.getBucketStories(bucketId, params);
      setBucketStories(prev => ({ ...prev, [bucketId]: data.items || [] }));
      // Counts come from the server across ALL statuses, so the filter bar
      // stays complete regardless of what this page happens to contain.
      if (data.statusCounts) {
        setBucketStatusCounts(prev => ({ ...prev, [bucketId]: data.statusCounts }));
      }
      if (data.statusCountsAnyArchive) {
        setBucketStatusCountsAnyArchive(prev => ({ ...prev, [bucketId]: data.statusCountsAnyArchive }));
      }
    } catch (err) {
      console.error(`Failed to load stories for bucket ${bucketId}:`, err);
    }
  }, [archiveFilters]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    const loadedBuckets = await loadBuckets();
    await Promise.all(loadedBuckets.map(b => loadBucketStories(b._id)));
    setLoading(false);
  }, [loadBuckets, loadBucketStories]);

  // ── In Production loader ───────────────────────────────────

  const inProdSyncSeqRef = useRef(0);
  const inProdSyncAbortRef = useRef(null);
  // Map<bucketStoryId, remoteItem|null> of remote updates withheld because the
  // row was being edited by the local user. `null` means "server removed it".
  const inProdPendingRef = useRef(new Map());
  // Stored as state so React re-renders the small "↻ updated remotely" hint.
  // Always a Map; identity changes when pendingRef changes meaningfully.
  const [inProdPending, setInProdPending] = useState(() => new Map());
  // Row id whose action menu is currently open. Used as an active id alongside
  // the modal states so the row is frozen during the user's interaction.
  const [openMenuItemId, setOpenMenuItemId] = useState(null);
  // Row id of a currently focused form control (e.g. productionType select).
  const [focusedRowId, setFocusedRowId] = useState(null);

  const loadInProduction = useCallback(async (page = 1, statusFilter = null) => {
    try {
      setLoadingInProd(true);
      // Bump the sync sequence so any in-flight background sync is treated as stale
      // and won't overwrite the fresh full-load response.
      inProdSyncSeqRef.current += 1;
      if (inProdSyncAbortRef.current) {
        try { inProdSyncAbortRef.current.abort(); } catch (_) { /* noop */ }
        inProdSyncAbortRef.current = null;
      }
      const params = { page, limit: 50 };
      if (statusFilter) params.status = statusFilter;
      const data = await api.getInProductionDashboard(params);
      setInProdItems(data.items || []);
      setInProdTotal(data.total || 0);
      setInProdPage(page);
      if (data.statusCounts) setInProdStatusCounts(data.statusCounts);
    } catch (err) {
      setToast({ message: 'Failed to load in-production items', type: 'error' });
    } finally {
      setLoadingInProd(false);
    }
  }, []);

  // ── Published loader ───────────────────────────────────────

  const loadPublished = useCallback(async (page = 1) => {
    try {
      setLoadingPublished(true);
      const data = await api.getInProductionDashboard({ page, limit: 50, status: 'published' });
      setPublishedItems(data.items || []);
      setPublishedTotal(data.total || 0);
      setPublishedPage(page);
    } catch (err) {
      setToast({ message: 'Failed to load published items', type: 'error' });
    } finally {
      setLoadingPublished(false);
    }
  }, []);

  // ── Recommendation handlers ───────────────────────────────

  const loadRecommendations = useCallback(async (bucketId) => {
    if (!bucketId) return;
    try {
      setLoadingRecs(true);
      setRecSelected(new Set());
      const data = await api.getBucketRecommendations(bucketId);
      setRecommendations(data.recommendations || []);
      setRecGenerationKey(data.generationKey || null);
    } catch (err) {
      setToast({ message: 'Failed to load recommendations', type: 'error' });
    } finally {
      setLoadingRecs(false);
    }
  }, []);

  const handleRefreshRecs = useCallback(async () => {
    if (!recBucketId) return;
    try {
      setRefreshingRecs(true);
      const hasExisting = recGenerationKey != null;
      const data = hasExisting
        ? await api.refreshBucketRecommendations(recBucketId)
        : await api.generateBucketRecommendations(recBucketId);
      setRecommendations(data.recommendations || []);
      setRecGenerationKey(data.generationKey || null);
      setRecSelected(new Set());
      const count = data.recommendations?.length || 0;
      setToast({ message: `${count} recommendation${count !== 1 ? 's' : ''} generated`, type: 'success' });
    } catch (err) {
      setToast({ message: 'Failed to generate recommendations', type: 'error' });
    } finally {
      setRefreshingRecs(false);
    }
  }, [recBucketId, recGenerationKey]);

  const handleDismissRec = useCallback(async (recId) => {
    if (!recBucketId) return;
    try {
      await api.dismissBucketRecommendation(recBucketId, recId);
      setRecommendations(prev => prev.filter(r => (r._id || r.recommendationId) !== recId));
      setRecSelected(prev => { const next = new Set(prev); next.delete(recId); return next; });
    } catch (err) {
      setToast({ message: 'Failed to dismiss recommendation', type: 'error' });
    }
  }, [recBucketId]);

  const handleAddOneRec = useCallback(async (recId) => {
    if (!recBucketId) return;
    try {
      setRecAdding(recId);
      const result = await api.addRecommendationToBucket(recBucketId, recId);
      if (result.duplicate) {
        setToast({ message: 'Story already in bucket', type: 'warning' });
      } else {
        setToast({ message: 'Recommendation added to bucket', type: 'success' });
      }
      setRecommendations(prev => prev.map(r =>
        (r._id || r.recommendationId) === recId ? { ...r, addedToBucketAt: new Date().toISOString() } : r
      ));
    } catch (err) {
      setToast({ message: 'Failed to add recommendation', type: 'error' });
    } finally {
      setRecAdding(null);
    }
  }, [recBucketId]);

  const handleAddSelectedRecs = useCallback(async () => {
    if (!recBucketId || recSelected.size === 0) return;
    try {
      setRecBulkAdding(true);
      const ids = Array.from(recSelected);
      const result = await api.addSelectedRecommendationsToBucket(recBucketId, ids);
      const s = result.summary || {};
      const parts = [];
      if (s.added) parts.push(`${s.added} added`);
      if (s.duplicates) parts.push(`${s.duplicates} already in bucket`);
      if (s.failed) parts.push(`${s.failed} failed`);
      setToast({ message: parts.join(', ') || 'Done', type: s.failed ? 'warning' : 'success' });
      setRecSelected(new Set());
      await loadRecommendations(recBucketId);
    } catch (err) {
      setToast({ message: 'Failed to add selected recommendations', type: 'error' });
    } finally {
      setRecBulkAdding(false);
    }
  }, [recBucketId, recSelected, loadRecommendations]);

  const handleRecBucketChange = useCallback((bucketId) => {
    setRecBucketId(bucketId);
    if (bucketId) loadRecommendations(bucketId);
  }, [loadRecommendations]);

  useEffect(() => {
    if (!canEdit) {
      navigate('/', { replace: true });
      return;
    }
    if (activeView === 'board') {
      loadAllData();
    } else if (activeView === 'inProduction') {
      loadInProduction(1, null);
      setInProdStatusFilter(null);
      if (buckets.length === 0) loadBuckets();
      if (!usersLoaded) loadAssignableUsers();
    } else if (activeView === 'published') {
      loadPublished(1);
      if (buckets.length === 0) loadBuckets();
    } else if (activeView === 'recommended') {
      if (buckets.length === 0) loadBuckets();
      if (recBucketId) loadRecommendations(recBucketId);
    }
  }, [canEdit, navigate, activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── In Production live sync (background polling) ───────────
  //
  // Refresh the visible In Production list at a configurable interval so
  // collaborators see each other's status/owner/feedback changes without
  // hitting Refresh. The merge preserves the React table in place — no
  // unmount, no scroll jump, no closed modals — and freezes rows that the
  // current user is actively editing (open menu, open modal, focused field).
  // Non-active rows still update and new items still appear during edits.
  const inProdActiveIds = useMemo(() => {
    const set = new Set();
    const add = (id) => { if (id) set.add(String(id)); };
    if (assignModal?.bucketStoryId) add(assignModal.bucketStoryId);
    if (moveModal?.inProdMode) add(moveModal.item?._id || moveModal.item?.bucketStoryId);
    if (removeModal?.item?._id) add(removeModal.item._id);
    if (openMenuItemId) add(openMenuItemId);
    if (focusedRowId) add(focusedRowId);
    return set;
  }, [assignModal, moveModal, removeModal, openMenuItemId, focusedRowId]);

  const runInProdSync = useCallback(async ({ forceImmediate = false } = {}) => {
    if (activeView !== 'inProduction') return;
    if (document.hidden && !forceImmediate) return;
    if (loadingInProd) return;

    if (inProdSyncAbortRef.current) {
      try { inProdSyncAbortRef.current.abort(); } catch (_) { /* noop */ }
    }
    const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    inProdSyncAbortRef.current = controller;
    const seq = ++inProdSyncSeqRef.current;

    try {
      const params = { page: inProdPage, limit: 50 };
      if (inProdStatusFilter) params.status = inProdStatusFilter;
      const cfg = controller ? { signal: controller.signal } : {};
      const data = await api.getInProductionDashboard(params, cfg);
      if (seq !== inProdSyncSeqRef.current) return; // stale response

      const activeIds = inProdActiveIds;
      setInProdItems(prev => {
        const { items, pending } = mergeInProdItems(prev, data.items || [], { activeIds });
        if (!pendingMapsEqual(inProdPendingRef.current, pending)) {
          inProdPendingRef.current = pending;
          setInProdPending(pending);
        }
        return items;
      });
      if (typeof data.total === 'number') setInProdTotal(data.total);
      if (data.statusCounts) setInProdStatusCounts(data.statusCounts);
    } catch (err) {
      const name = err && (err.name || err.code);
      if (name === 'CanceledError' || name === 'AbortError' || name === 'ERR_CANCELED') return;
      // Background sync failure isn't user-actionable — swallow quietly.
      console.warn('[BucketManager] In-Production live sync failed:', err && err.message);
    }
  }, [activeView, loadingInProd, inProdPage, inProdStatusFilter, inProdActiveIds]);

  useEffect(() => {
    if (activeView !== 'inProduction') return undefined;
    if (!liveSyncIntervalMs || liveSyncIntervalMs <= 0) return undefined;

    const intervalId = setInterval(() => { runInProdSync(); }, liveSyncIntervalMs);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') runInProdSync({ forceImmediate: true });
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      if (inProdSyncAbortRef.current) {
        try { inProdSyncAbortRef.current.abort(); } catch (_) { /* noop */ }
        inProdSyncAbortRef.current = null;
      }
    };
  }, [activeView, liveSyncIntervalMs, runInProdSync]);

  // When the active set shrinks (modal closed, menu collapsed, focus left),
  // immediately reconcile so any pending remote updates land in the visible list.
  const inProdPrevActiveIdsRef = useRef(inProdActiveIds);
  useEffect(() => {
    if (activeView !== 'inProduction') {
      inProdPrevActiveIdsRef.current = inProdActiveIds;
      return;
    }
    const prevIds = inProdPrevActiveIdsRef.current;
    let shrank = false;
    for (const id of prevIds) {
      if (!inProdActiveIds.has(id)) { shrank = true; break; }
    }
    inProdPrevActiveIdsRef.current = inProdActiveIds;
    if (shrank) runInProdSync({ forceImmediate: true });
  }, [inProdActiveIds, activeView, runInProdSync]);

  // ── Ordered + filtered buckets ─────────────────────────────

  const orderedBuckets = (() => {
    if (bucketOrder.length === 0) return buckets;
    const map = {};
    for (const b of buckets) map[b._id] = b;
    const ordered = bucketOrder.filter(id => map[id]).map(id => map[id]);
    // Append any new buckets not in order
    for (const b of buckets) {
      if (!bucketOrder.includes(b._id)) ordered.push(b);
    }
    return ordered;
  })();

  const visibleBuckets = orderedBuckets.filter(b => visibilityMap[b._id] !== false);

  // Auto-expand the working bucket (first visible) into slot 0
  useEffect(() => {
    if (visibleBuckets.length > 0) {
      const workingId = visibleBuckets[0]._id;
      setOpenSlots(prev => {
        if (prev[0] === workingId) return prev;
        return [workingId, ...prev.filter(id => id !== workingId).slice(0, 2)];
      });
    }
  }, [visibleBuckets]);

  // Helper: is a bucket in an open slot?
  const isInOpenSlot = useCallback((bucketId) => openSlots.includes(bucketId), [openSlots]);

  // Open a bucket into a side slot (FIFO on slots 1&2, slot 0 stays)
  const openBucketSlot = useCallback((bucketId) => {
    setOpenSlots(prev => {
      if (prev.includes(bucketId)) return prev;
      const slot0 = prev[0]; // working bucket stays
      const side = prev.slice(1).filter(id => id !== bucketId);
      side.push(bucketId);
      // FIFO: if more than 2 side slots, evict oldest (first in)
      while (side.length > 2) side.shift();
      return [slot0, ...side];
    });
  }, []);

  // Close a bucket from its open slot (cannot close slot 0)
  const closeBucketSlot = useCallback((bucketId) => {
    setOpenSlots(prev => {
      if (prev[0] === bucketId) return prev; // cannot close working bucket
      return prev.filter(id => id !== bucketId);
    });
  }, []);

  // Swap a dragged bucket into an open slot (replace current occupant)
  const handleSwapToOpenSlot = useCallback((slotIdx, draggedBucketId) => {
    setOpenSlots(prev => {
      const next = [...prev];
      // Remove dragged bucket if it's already in another open slot
      const existingIdx = next.indexOf(draggedBucketId);
      if (existingIdx !== -1) {
        // Swap the two open slots
        const displaced = next[slotIdx];
        next[slotIdx] = draggedBucketId;
        next[existingIdx] = displaced;
      } else {
        // Replace the slot occupant with the dragged bucket
        next[slotIdx] = draggedBucketId;
      }
      return next;
    });
    // If replacing slot 0 (working bucket), also update bucket order so it stays first
    if (slotIdx === 0) {
      setBucketOrder(prev => {
        const order = prev.filter(id => id !== draggedBucketId);
        order.unshift(draggedBucketId);
        saveBucketOrder(order);
        return order;
      });
    }
  }, []);

  // ── Bucket visibility toggle ───────────────────────────────

  const toggleBucketVisibility = useCallback((bucketId) => {
    setVisibilityMap(prev => {
      const next = { ...prev, [bucketId]: prev[bucketId] === false ? true : false };
      saveVisibility(next);
      return next;
    });
  }, []);

  // ── Bucket reorder (drag columns) ─────────────────────────

  const handleBucketReorder = useCallback((fromIndex, toIndex) => {
    // Guard: never displace open/pinned bucket positions
    const minIdx = openSlots.length;
    if (fromIndex < minIdx || toIndex < minIdx) return;

    const ids = visibleBuckets.map(b => b._id);
    const [moved] = ids.splice(fromIndex, 1);
    ids.splice(toIndex, 0, moved);
    // Rebuild full order: visible in new order + hidden buckets keep their position
    const hiddenIds = orderedBuckets.filter(b => visibilityMap[b._id] === false).map(b => b._id);
    const fullOrder = [...ids, ...hiddenIds];
    setBucketOrder(fullOrder);
    saveBucketOrder(fullOrder);
  }, [visibleBuckets, orderedBuckets, visibilityMap, openSlots]);

  // ── Status change ──────────────────────────────────────────

  const handleStatusChange = useCallback(async (bucketStoryId, newStatus) => {
    try {
      await api.changeBucketStoryStatus(bucketStoryId, newStatus);
      setBucketStories(prev => {
        const updated = { ...prev };
        for (const bid of Object.keys(updated)) {
          updated[bid] = updated[bid].map(item =>
            item._id === bucketStoryId ? { ...item, status: newStatus } : item
          );
        }
        return updated;
      });
      setToast({ message: `Status changed to ${STATUS_LABELS[newStatus]}`, type: 'success' });
    } catch (err) {
      setToast({ message: 'Failed to change status', type: 'error' });
    }
  }, []);

  // ── Remove (with confirmation) ─────────────────────────────

  const requestRemove = useCallback((item, bucketId) => {
    const bucket = buckets.find(b => b._id === bucketId);
    setRemoveModal({ item, bucketId, bucketName: bucket?.name || 'Unknown' });
  }, [buckets]);

  const confirmRemove = useCallback(async () => {
    if (!removeModal) return;
    try {
      await api.removeBucketStory(removeModal.item._id);
      setBucketStories(prev => {
        const updated = { ...prev };
        for (const bid of Object.keys(updated)) {
          updated[bid] = updated[bid].filter(i => i._id !== removeModal.item._id);
        }
        return updated;
      });
      setToast({ message: 'Story removed from bucket', type: 'success' });
    } catch (err) {
      setToast({ message: 'Failed to remove story', type: 'error' });
    }
    setRemoveModal(null);
  }, [removeModal]);

  // ── Copy ───────────────────────────────────────────────────

  const handleCopyTo = useCallback(async (item, destBucketId) => {
    try {
      await api.copyBucketStory(item._id, destBucketId);
      await loadBucketStories(destBucketId);
      setToast({ message: 'Story copied', type: 'success' });
    } catch (err) {
      if (err.response?.status === 409) {
        setToast({ message: 'Story already exists in destination bucket', type: 'warning' });
      } else {
        setToast({ message: 'Failed to copy story', type: 'error' });
      }
    }
  }, [loadBucketStories]);

  // ── Drop between columns = COPY (default), Shift+drag = MOVE ──

  const handleDropFromOther = useCallback(async (dragged, destBucketId) => {
    const items = bucketStories[dragged.sourceBucketId] || [];
    const item = items.find(i => i._id === dragged.bucketStoryId);
    if (!item) return;
    if (shiftKeyRef.current) {
      // Shift+drag = MOVE (open move modal with removal options)
      const sourceBucket = buckets.find(b => b._id === dragged.sourceBucketId);
      const destBucket = buckets.find(b => b._id === destBucketId);
      setMoveModal({
        item,
        sourceBucketName: sourceBucket?.name || 'Unknown',
        destBucketName: destBucket?.name || 'Unknown',
        destBucketId,
      });
    } else {
      // Default drag = COPY (keep in source bucket)
      try {
        await api.copyBucketStory(item._id, destBucketId);
        await loadBucketStories(destBucketId);
        openBucketSlot(destBucketId);
        setToast({ message: 'Story copied to bucket', type: 'success' });
      } catch (err) {
        if (err.response?.status === 409) {
          setToast({ message: 'Story already exists in destination bucket', type: 'warning' });
        } else {
          setToast({ message: 'Failed to copy story', type: 'error' });
        }
      }
    }
  }, [bucketStories, loadBucketStories, buckets]);

  // ── Move (via action menu) ─────────────────────────────────

  const handleMoveTo = useCallback((item, destBucketId, sourceBucketId) => {
    const sourceBucket = buckets.find(b => b._id === sourceBucketId);
    const destBucket = buckets.find(b => b._id === destBucketId);
    setMoveModal({
      item,
      sourceBucketName: sourceBucket?.name || 'Unknown',
      destBucketName: destBucket?.name || 'Unknown',
      destBucketId,
    });
  }, [buckets]);

  // Helper: update a single item in the inProdItems list without refetching
  const updateInProdItem = useCallback((bucketStoryId, updater) => {
    setInProdItems(prev => prev.map(it => it._id === bucketStoryId ? updater(it) : it));
  }, []);

  // Helper: remove item from inProdItems and adjust counts/total
  const removeInProdItem = useCallback((bucketStoryId) => {
    setInProdItems(prev => {
      const item = prev.find(it => it._id === bucketStoryId);
      if (item) {
        setInProdStatusCounts(c => ({ ...c, [item.status]: Math.max(0, (c[item.status] || 1) - 1) }));
        setInProdTotal(t => Math.max(0, t - 1));
      }
      return prev.filter(it => it._id !== bucketStoryId);
    });
  }, []);

  // Helper: adjust status counts when an item changes status
  const shiftInProdCount = useCallback((oldStatus, newStatus) => {
    setInProdStatusCounts(c => ({
      ...c,
      [oldStatus]: Math.max(0, (c[oldStatus] || 1) - 1),
      [newStatus]: (c[newStatus] || 0) + 1,
    }));
  }, []);

  const confirmMove = useCallback(async (sourceMarkStatus) => {
    if (!moveModal) return;
    try {
      if (moveModal.copyMode) {
        await api.copyBucketStory(moveModal.item._id || moveModal.item.bucketStoryId, moveModal.destBucketId);
        setToast({ message: 'Story copied', type: 'success' });
      } else {
        await api.moveBucketStory(moveModal.item._id || moveModal.item.bucketStoryId, moveModal.destBucketId, sourceMarkStatus);
        setToast({ message: 'Story moved successfully', type: 'success' });
      }
      if (moveModal.inProdMode) {
        if (!moveModal.copyMode) {
          removeInProdItem(moveModal.item._id || moveModal.item.bucketStoryId);
        }
      } else {
        const sourceBucket = buckets.find(b => b.name === moveModal.sourceBucketName);
        await Promise.all([
          sourceBucket ? loadBucketStories(sourceBucket._id) : Promise.resolve(),
          loadBucketStories(moveModal.destBucketId),
        ]);
        openBucketSlot(moveModal.destBucketId);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setToast({ message: 'Story already exists in destination bucket', type: 'warning' });
      } else {
        setToast({ message: moveModal.copyMode ? 'Failed to copy story' : 'Failed to move story', type: 'error' });
      }
    }
    setMoveModal(null);
  }, [moveModal, buckets, loadBucketStories, removeInProdItem]);

  // ── Reorder within bucket ──────────────────────────────────

  const handleReorder = useCallback((bucketId, fromIndex, toIndex) => {
    setBucketStories(prev => {
      const items = [...(prev[bucketId] || [])];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);
      return { ...prev, [bucketId]: items };
    });
  }, []);

  const reorderTimeout = useRef(null);
  const persistReorder = useCallback((bucketId, movedStatus) => {
    if (reorderTimeout.current) clearTimeout(reorderTimeout.current);
    reorderTimeout.current = setTimeout(async () => {
      const items = bucketStories[bucketId] || [];
      // Only send IDs within the same status group as the moved item
      const group = getStatusGroup(movedStatus);
      const groupItems = items.filter(i => getStatusGroup(i.status) === group);
      const orderedIds = groupItems.map(i => i._id);
      try {
        const result = await api.reorderBucketStories(bucketId, orderedIds);
        if (result?.error) {
          setToast({ message: result.error, type: 'error' });
          await loadBucketStories(bucketId);
        }
      } catch (err) {
        console.error('Failed to save order:', err);
        setToast({ message: err.response?.data?.error || 'Failed to save order', type: 'error' });
        await loadBucketStories(bucketId);
      }
    }, 500);
  }, [bucketStories, loadBucketStories]);

  const handleReorderAndPersist = useCallback((bucketId, from, to) => {
    // Determine the status of the moved item before the reorder
    const items = bucketStories[bucketId] || [];
    const movedStatus = items[from]?.status;
    handleReorder(bucketId, from, to);
    persistReorder(bucketId, movedStatus);
  }, [handleReorder, persistReorder, bucketStories]);



  // ── Status filter ──────────────────────────────────────────

  const handleStatusFilterChange = useCallback((bucketId, status) => {
    setStatusFilters(prev => ({ ...prev, [bucketId]: status }));
    statusFiltersRef.current = { ...statusFiltersRef.current, [bucketId]: status };
    // Refetch — the chosen status may have no rows on the currently loaded page.
    loadBucketStories(bucketId, undefined, undefined, status);
  }, [loadBucketStories]);

  // ── Selection ──────────────────────────────────────────────

  const lastCheckedIndexRef = useRef({});

  const handleToggleSelect = useCallback((bucketId, storyId, meta) => {
    const shiftKey = !!meta?.shiftKey;
    const index = typeof meta?.index === 'number' ? meta.index : null;
    const visibleIds = Array.isArray(meta?.visibleIds) ? meta.visibleIds : null;
    const lastIdx = lastCheckedIndexRef.current[bucketId];

    setSelectedMap(prev => {
      const set = new Set(prev[bucketId] || []);

      if (shiftKey && visibleIds && index !== null && typeof lastIdx === 'number' && lastIdx !== index) {
        // Range select between lastIdx and index based on the toggled item's new state
        const willSelect = !set.has(storyId);
        const start = Math.min(lastIdx, index);
        const end = Math.max(lastIdx, index);
        for (let i = start; i <= end; i++) {
          const id = visibleIds[i];
          if (!id) continue;
          if (willSelect) set.add(id);
          else set.delete(id);
        }
      } else if (set.has(storyId)) {
        set.delete(storyId);
      } else {
        set.add(storyId);
      }

      return { ...prev, [bucketId]: set };
    });

    if (index !== null) {
      lastCheckedIndexRef.current = { ...lastCheckedIndexRef.current, [bucketId]: index };
    }
  }, []);

  // ── Select all / deselect all ──────────────────────────────

  const handleSelectAll = useCallback((bucketId, visibleStories = []) => {
    const stories = visibleStories.length > 0 ? visibleStories : (bucketStories[bucketId] || []);
    const storyIds = stories.map((story) => story._id);
    const currentSelected = selectedMap[bucketId] || new Set();
    const allSelected = storyIds.length > 0 && storyIds.every((storyId) => currentSelected.has(storyId));
    setSelectedMap(prev => {
      const next = new Set(prev[bucketId] || []);
      if (allSelected) {
        storyIds.forEach((storyId) => next.delete(storyId));
      } else {
        storyIds.forEach((storyId) => next.add(storyId));
      }
      return { ...prev, [bucketId]: next };
    });
  }, [bucketStories, selectedMap]);

  // ── Bulk status change ─────────────────────────────────────

  const handleBulkStatusChange = useCallback(async (bucketId, newStatus, visibleStories = []) => {
    const visibleIds = new Set((visibleStories.length > 0 ? visibleStories : (bucketStories[bucketId] || [])).map((story) => story._id));
    const ids = Array.from(selectedMap[bucketId] || []).filter((storyId) => visibleIds.has(storyId));
    if (ids.length === 0) return;
    try {
      await api.bulkChangeBucketStoryStatus(bucketId, ids, newStatus);
      setSelectedMap(prev => ({ ...prev, [bucketId]: new Set() }));
      await loadBucketStories(bucketId);
      setToast({ message: `${ids.length} stories changed to ${STATUS_LABELS[newStatus]}`, type: 'success' });
    } catch (err) {
      setToast({ message: 'Failed to bulk change status', type: 'error' });
    }
  }, [bucketStories, selectedMap, loadBucketStories]);

  // ── Archive/Unarchive ──────────────────────────────────────

  const handleArchive = useCallback(async (bucketStoryId, bucketId) => {
    try {
      await api.archiveBucketStory(bucketStoryId);
      await loadBucketStories(bucketId, searchQueries[bucketId]);
      setToast({ message: 'Story archived', type: 'success' });
    } catch (err) {
      setToast({ message: 'Failed to archive story', type: 'error' });
    }
  }, [loadBucketStories, searchQueries]);

  const handleUnarchive = useCallback(async (bucketStoryId, bucketId) => {
    try {
      await api.unarchiveBucketStory(bucketStoryId);
      await loadBucketStories(bucketId, searchQueries[bucketId]);
      setToast({ message: 'Story unarchived', type: 'success' });
    } catch (err) {
      setToast({ message: 'Failed to unarchive story', type: 'error' });
    }
  }, [loadBucketStories, searchQueries]);

  // ── Archive filter change ──────────────────────────────────

  const handleArchiveFilterChange = useCallback((bucketId, value) => {
    setArchiveFilters(prev => ({ ...prev, [bucketId]: value }));
    loadBucketStories(bucketId, searchQueries[bucketId], value);
  }, [loadBucketStories, searchQueries]);

  // ── Copy list (auto-create snapshot + copy text list) ────────

  const handleShare = useCallback(async (bucketId) => {
    setSharingBucket(bucketId);
    try {
      const result = await api.createSuggestionList(bucketId, true, 'copy_list');
      if (result?.list?.items?.length > 0) {
        const text = result.list.items.map(it => `${it.suggestionNumber}. ${it.titleSnapshot}`).join('\n');
        await navigator.clipboard.writeText(text);
        setToast({ message: `Snapshot created (${result.itemCount} items). List copied!`, type: 'success' });
        // Reload stories to reflect newly assigned suggestionNumbers
        loadBucketStories(bucketId);
      } else {
        setToast({ message: 'No suggestions to copy', type: 'info' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to create snapshot', type: 'error' });
    }
    setSharingBucket(null);
  }, [loadBucketStories]);

  // ── Copy link (auto-create snapshot + copy public link) ─────

  const handleCopyPublicLink = useCallback(async (bucketId) => {
    setSharingBucket(bucketId);
    try {
      const url = await clipboardWriteDeferred(async () => {
        const result = await api.createSuggestionList(bucketId, true, 'copy_link');
        if (result?.list?.publicToken) {
          loadBucketStories(bucketId);
          return `${window.location.origin}/suggestions/${result.list.publicToken}`;
        }
        return '';
      });
      if (url) {
        setToast({ message: 'Public snapshot link copied!', type: 'success' });
      } else {
        setToast({ message: 'No suggestions to share', type: 'info' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to create link', type: 'error' });
    }
    setSharingBucket(null);
  }, [loadBucketStories]);

  // ── Send link (auto-create snapshot + send to publisher) ────

  // Send link, on the board. Opens the SHARED confirmation rather than
  // sending immediately.
  //
  // It used to fire the send straight from the button with no dialog, so
  // nobody saw who it was going to and there was no way to choose a channel.
  // Adi asked for WhatsApp from a bucket page on 5 November; this page is one
  // of three that send the same link, and a copy of the dialog in each is how
  // a later change reaches one and misses two (CLAUDE.md Gotcha 25).
  const handleSendToPublisher = useCallback((bucketId) => {
    setSendModalBucket(bucketId);
  }, []);


  // ── Assign to user / Unassign ────────────────────────────────

  const handleOpenAssign = useCallback((item) => {
    loadAssignableUsers();
    setAssignModal({
      bucketStoryId: item._id,
      currentAssignee: item.assignedAdminUserId,
      storyTitle: item.story?.title || 'Unknown story',
    });
    setSelectedAssigneeId(item.assignedAdminUserId || '');
    setAssignSearch('');
  }, [loadAssignableUsers]);

  /**
   * Picking a name IS the assignment.
   *
   * The modal used to make the choice twice: click the row to select it, then
   * click Assign to commit. Nothing happens between those two clicks — there is
   * no second field to fill and nothing to review — so the button was a step
   * that could only be forgotten. The row click now writes straight through and
   * closes on success; a failure leaves the modal open with the previous
   * selection restored, so the choice is not silently lost.
   *
   * The id is passed in rather than read from `selectedAssigneeId`: that state
   * update has not landed yet inside the same handler, so reading it would
   * assign the PREVIOUS selection.
   */
  const handleAssignPick = useCallback(async (assigneeId) => {
    if (!assignModal || assignSaving) return;
    const previous = selectedAssigneeId;
    setSelectedAssigneeId(assigneeId);
    setAssignSaving(true);
    try {
      await api.assignBucketStoryAdmin(assignModal.bucketStoryId, assigneeId || null);
      if (activeView === 'inProduction') {
        updateInProdItem(assignModal.bucketStoryId, it => ({ ...it, assignedAdminUserId: assigneeId || null }));
      } else {
        setBucketStories(prev => {
          const updated = { ...prev };
          for (const bid of Object.keys(updated)) {
            updated[bid] = updated[bid].map(item =>
              item._id === assignModal.bucketStoryId ? { ...item, assignedAdminUserId: assigneeId || null } : item
            );
          }
          return updated;
        });
      }
      setToast({ message: assigneeId ? 'User assigned' : 'Assignment cleared', type: 'success' });
      setAssignModal(null);
    } catch (err) {
      setSelectedAssigneeId(previous);
      setToast({ message: 'Failed to assign', type: 'error' });
    } finally {
      setAssignSaving(false);
    }
  }, [assignModal, assignSaving, selectedAssigneeId, activeView, updateInProdItem]);

  // ── Bulk delete (remove from bucket) ───────────────────────

  const [bulkDeleteModal, setBulkDeleteModal] = useState(null);

  const requestBulkDelete = useCallback((bucketId, visibleStories = []) => {
    const visibleIds = new Set((visibleStories.length > 0 ? visibleStories : (bucketStories[bucketId] || [])).map((story) => story._id));
    const ids = Array.from(selectedMap[bucketId] || []).filter((storyId) => visibleIds.has(storyId));
    if (ids.length === 0) return;
    const bucket = buckets.find(b => b._id === bucketId);
    setBulkDeleteModal({ bucketId, bucketName: bucket?.name || 'Unknown', ids });
  }, [bucketStories, selectedMap, buckets]);

  const confirmBulkDelete = useCallback(async () => {
    if (!bulkDeleteModal) return;
    const { bucketId, ids } = bulkDeleteModal;
    try {
      const results = await Promise.allSettled(ids.map((id) => api.removeBucketStory(id)));
      const removed = new Set(
        ids.filter((_, i) => results[i].status === 'fulfilled')
      );
      const failed = ids.length - removed.size;
      setBucketStories(prev => {
        const updated = { ...prev };
        for (const bid of Object.keys(updated)) {
          updated[bid] = updated[bid].filter(i => !removed.has(i._id));
        }
        return updated;
      });
      setSelectedMap(prev => ({ ...prev, [bucketId]: new Set() }));
      if (failed === 0) {
        setToast({ message: `${removed.size} stories removed`, type: 'success' });
      } else {
        setToast({ message: `Removed ${removed.size}, ${failed} failed`, type: 'warning' });
      }
    } catch (err) {
      setToast({ message: 'Failed to remove stories', type: 'error' });
    }
    setBulkDeleteModal(null);
  }, [bulkDeleteModal]);

  // ── Bulk archive ───────────────────────────────────────────

  const handleBulkArchive = useCallback(async (bucketId, visibleStories = []) => {
    const visibleIds = new Set((visibleStories.length > 0 ? visibleStories : (bucketStories[bucketId] || [])).map((story) => story._id));
    const ids = Array.from(selectedMap[bucketId] || []).filter((storyId) => visibleIds.has(storyId));
    if (ids.length === 0) return;
    try {
      await api.bulkArchiveBucketStories(bucketId, ids, true);
      setSelectedMap(prev => ({ ...prev, [bucketId]: new Set() }));
      await loadBucketStories(bucketId, searchQueries[bucketId]);
      setToast({ message: `${ids.length} stories archived`, type: 'success' });
    } catch (err) {
      setToast({ message: 'Failed to archive stories', type: 'error' });
    }
  }, [bucketStories, selectedMap, loadBucketStories, searchQueries]);

  // ── One-click advance ──────────────────────────────────────

  const handleAdvanceStatus = useCallback(async (bucketStoryId) => {
    try {
      const result = await api.advanceBucketStoryStatus(bucketStoryId);
      const newStatus = result?.bucketStory?.status;
      setBucketStories(prev => {
        const updated = { ...prev };
        for (const bid of Object.keys(updated)) {
          updated[bid] = updated[bid].map(item =>
            item._id === bucketStoryId ? { ...item, status: newStatus || item.status } : item
          );
        }
        return updated;
      });
      if (newStatus) setToast({ message: `Advanced to ${STATUS_LABELS[newStatus]}`, type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Cannot advance status', type: 'warning' });
    }
  }, []);

  // ── Per-bucket search ──────────────────────────────────────

  const handleSearchChange = useCallback((bucketId, value) => {
    setSearchQueries(prev => ({ ...prev, [bucketId]: value }));
    if (searchTimeoutRef.current[bucketId]) clearTimeout(searchTimeoutRef.current[bucketId]);
    searchTimeoutRef.current[bucketId] = setTimeout(() => {
      loadBucketStories(bucketId, value);
    }, 350);
  }, [loadBucketStories]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (reorderTimeout.current) clearTimeout(reorderTimeout.current);
      Object.values(searchTimeoutRef.current).forEach(t => clearTimeout(t));
    };
  }, []);

  // ── In Production handlers ─────────────────────────────────

  const getUserName = useCallback((userId) => {
    if (!userId) return '—';
    const u = assignableUsers.find(x => x._id === userId || x.fronteggUserId === userId);
    return u ? (u.name || u.email) : userId.slice(0, 8) + '…';
  }, [assignableUsers]);

  const handleInProdMenuOpenChange = useCallback((bucketStoryId, isOpen) => {
    setOpenMenuItemId(prev => {
      if (isOpen) return bucketStoryId;
      if (prev === bucketStoryId) return null;
      return prev;
    });
  }, []);

  const handleInProdFilterChange = useCallback((status) => {
    const newFilter = inProdStatusFilter === status ? null : status;
    setInProdStatusFilter(newFilter);
    loadInProduction(1, newFilter);
  }, [inProdStatusFilter, loadInProduction]);

  const handleInProdStatusChange = useCallback(async (bucketStoryId, newStatus) => {
    try {
      await api.changeBucketStoryStatus(bucketStoryId, newStatus);
      setToast({ message: `Status changed to ${STATUS_LABELS[newStatus]}`, type: 'success' });
      const item = inProdItems.find(it => it._id === bucketStoryId);
      const oldStatus = item?.status;
      const isInProdStatus = IN_PROD_STATUSES.includes(newStatus);
      const matchesFilter = !inProdStatusFilter || newStatus === inProdStatusFilter;
      if (!isInProdStatus || !matchesFilter) {
        removeInProdItem(bucketStoryId);
        if (isInProdStatus && oldStatus) shiftInProdCount(oldStatus, newStatus);
      } else {
        if (oldStatus) shiftInProdCount(oldStatus, newStatus);
        updateInProdItem(bucketStoryId, it => ({ ...it, status: newStatus }));
      }
    } catch (err) {
      setToast({ message: 'Failed to change status', type: 'error' });
    }
  }, [inProdItems, inProdStatusFilter, updateInProdItem, removeInProdItem, shiftInProdCount]);

  const handleInProdAdvance = useCallback(async (bucketStoryId) => {
    try {
      const result = await api.advanceBucketStoryStatus(bucketStoryId);
      const newStatus = result?.bucketStory?.status || result?.newStatus;
      setToast({ message: `Advanced to ${STATUS_LABELS[newStatus] || newStatus}`, type: 'success' });
      const item = inProdItems.find(it => it._id === bucketStoryId);
      const oldStatus = item?.status;
      const isInProdStatus = IN_PROD_STATUSES.includes(newStatus);
      const matchesFilter = !inProdStatusFilter || newStatus === inProdStatusFilter;
      if (!isInProdStatus || !matchesFilter) {
        removeInProdItem(bucketStoryId);
        if (isInProdStatus && oldStatus) shiftInProdCount(oldStatus, newStatus);
      } else {
        if (oldStatus) shiftInProdCount(oldStatus, newStatus);
        updateInProdItem(bucketStoryId, it => ({ ...it, status: newStatus }));
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Cannot advance status', type: 'warning' });
    }
  }, [inProdItems, inProdStatusFilter, updateInProdItem, removeInProdItem, shiftInProdCount]);

  // Badge click: cycle within In Production statuses only (circular)
  const handleInProdBadgeCycle = useCallback(async (bucketStoryId) => {
    const item = inProdItems.find(it => it._id === bucketStoryId);
    if (!item) return;
    const nextStatus = IN_PROD_FORWARD[item.status];
    if (!nextStatus) return;
    try {
      await api.changeBucketStoryStatus(bucketStoryId, nextStatus);
      setToast({ message: `Status → ${STATUS_LABELS[nextStatus]}`, type: 'success' });
      const matchesFilter = !inProdStatusFilter || nextStatus === inProdStatusFilter;
      if (!matchesFilter) {
        removeInProdItem(bucketStoryId);
      } else {
        updateInProdItem(bucketStoryId, it => ({ ...it, status: nextStatus }));
      }
      shiftInProdCount(item.status, nextStatus);
    } catch (err) {
      setToast({ message: 'Failed to change status', type: 'error' });
    }
  }, [inProdItems, inProdStatusFilter, updateInProdItem, removeInProdItem, shiftInProdCount]);

  const handleInProdRemove = useCallback(async (bucketStoryId) => {
    try {
      await api.removeBucketStory(bucketStoryId);
      setToast({ message: 'Story removed', type: 'success' });
      removeInProdItem(bucketStoryId);
    } catch (err) {
      setToast({ message: 'Failed to remove story', type: 'error' });
    }
  }, [removeInProdItem]);

  const handleInProdArchive = useCallback(async (bucketStoryId, isArchived) => {
    try {
      if (isArchived) {
        await api.unarchiveBucketStory(bucketStoryId);
        setToast({ message: 'Story unarchived', type: 'success' });
      } else {
        await api.archiveBucketStory(bucketStoryId);
        setToast({ message: 'Story archived', type: 'success' });
      }
      updateInProdItem(bucketStoryId, it => ({ ...it, isArchived: !isArchived }));
    } catch (err) {
      setToast({ message: 'Failed to update archive status', type: 'error' });
    }
  }, [updateInProdItem]);

  const handleInProdProductionType = useCallback(async (bucketStoryId, newType) => {
    try {
      await api.changeBucketStoryProductionType(bucketStoryId, newType);
      setToast({ message: `Production type changed to ${newType}`, type: 'success' });
      updateInProdItem(bucketStoryId, it => ({ ...it, productionType: newType }));
    } catch (err) {
      setToast({ message: 'Failed to change production type', type: 'error' });
    }
  }, [updateInProdItem]);

  const handleInProdOpenAssign = useCallback((item) => {
    loadAssignableUsers();
    setAssignModal({
      bucketStoryId: item._id,
      currentAssignee: item.assignedAdminUserId,
      storyTitle: item.story?.title || 'Unknown story',
    });
    setSelectedAssigneeId(item.assignedAdminUserId || '');
    setAssignSearch('');
  }, [loadAssignableUsers]);

  const handleInProdMove = useCallback((item) => {
    const sourceBucket = buckets.find(b => b._id === (item.publisherBucketId || item.bucketId?._id || item.bucketId));
    setMoveModal({
      item,
      sourceBucketName: sourceBucket?.name || item.bucket?.name || 'Unknown',
      destBucketName: '',
      destBucketId: null,
      inProdMode: true,
    });
  }, [buckets]);

  const handleInProdCopy = useCallback((item) => {
    setMoveModal({
      item,
      sourceBucketName: item.bucket?.name || 'Unknown',
      destBucketName: '',
      destBucketId: null,
      inProdMode: true,
      copyMode: true,
    });
  }, []);

  // ── Render ─────────────────────────────────────────────────

  if (!canEdit) return null;

  const showInProdPageViews = inProdItems.some((item) => Number(item.latestPageViews) > 0);
  const showInProdFinalUrl = inProdItems.some((item) => Boolean(item.publicationUrl));
  const showPublishedPageViews = publishedItems.some((item) => Number(item.latestPageViews) > 0);
  const showPublishedFinalUrl = publishedItems.some((item) => Boolean(item.publicationUrl));

  return (
    <div className="bucket-manager">
      <div className="bucket-manager-toolbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <a href="/" className="bucket-manager-back"><ArrowBackIcon style={{ fontSize: 16 }} /> Stories</a>
          <h2>Publisher Buckets</h2>
        </div>
        <div className="bucket-manager-toolbar-actions">
          <div className="bucket-view-toggle">
            <button
              className={`bucket-view-btn ${activeView === 'board' ? 'active' : ''}`}
              onClick={() => setActiveView('board')}
            >
              <ViewColumnOutlinedIcon style={{ fontSize: 14 }} /> Board
            </button>
            <button
              className={`bucket-view-btn ${activeView === 'inProduction' ? 'active' : ''}`}
              onClick={() => setActiveView('inProduction')}
            >
              <FastForwardOutlinedIcon style={{ fontSize: 14 }} /> In Production
            </button>
            <button
              className={`bucket-view-btn ${activeView === 'published' ? 'active' : ''}`}
              onClick={() => setActiveView('published')}
            >
              <CheckCircleOutlineIcon style={{ fontSize: 14 }} /> Published
            </button>
            <button
              className={`bucket-view-btn ${activeView === 'recommended' ? 'active' : ''}`}
              onClick={() => setActiveView('recommended')}
            >
              <AutoAwesomeOutlinedIcon style={{ fontSize: 14 }} /> Recommended
            </button>
          </div>
          {activeView === 'board' && (
            <>
              <button
                className="bucket-col-btn"
                onClick={() => setSidebarOpen(o => !o)}
                title="Filter buckets"
              >
                <SearchOutlinedIcon style={{ fontSize: 14 }} /> Filter
              </button>
              <button
                className="bucket-col-btn"
                onClick={loadAllData}
                title="Refresh all buckets"
              >
                <RefreshOutlinedIcon style={{ fontSize: 14 }} /> Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {activeView === 'board' && (
      <div className="bucket-manager-body">
        {/* Left sidebar filter */}
        <BucketFilterSidebar
          buckets={orderedBuckets}
          visibilityMap={visibilityMap}
          onToggle={toggleBucketVisibility}
          sidebarOpen={sidebarOpen}
          onSidebarToggle={() => setSidebarOpen(o => !o)}
        />

        {/* Main board area */}
        {loading ? (
          <div className="bucket-manager-loading">
            <div className="bucket-manager-spinner" />
            Loading buckets...
          </div>
        ) : visibleBuckets.length === 0 ? (
          <div className="bucket-manager-loading">
            {buckets.length === 0 ? 'No buckets found. Create buckets in Admin panel.' : 'All buckets hidden. Use Filter to show buckets.'}
          </div>
        ) : (() => {
          const openBuckets = openSlots
            .map(id => visibleBuckets.find(b => b._id === id))
            .filter(Boolean);
          const openSet = new Set(openSlots);
          const closedBuckets = visibleBuckets.filter(b => !openSet.has(b._id));
          return (
          <div className="bucket-board">
            {/* Open bucket slots — up to 3, side by side */}
            <div className="bucket-board-open">
              {openBuckets.map((bucket, slotIdx) => (
                <DroppableOpenSlot
                  key={bucket._id}
                  slotIdx={slotIdx}
                  currentBucketId={bucket._id}
                  onSwap={handleSwapToOpenSlot}
                  className={`bucket-open-slot bucket-open-slot-${slotIdx}`}
                >
                  <BucketColumn
                    bucket={bucket}
                    stories={bucketStories[bucket._id] || []}
                    allBuckets={buckets}
                    onDropFromOther={handleDropFromOther}
                    onReorder={handleReorderAndPersist}
                    onStatusChange={handleStatusChange}
                    onAdvanceStatus={handleAdvanceStatus}
                    onRemove={requestRemove}
                    onCopyTo={handleCopyTo}
                    onMoveTo={handleMoveTo}
                    statusFilter={statusFilters[bucket._id] || null}
                    onStatusFilterChange={handleStatusFilterChange}
                    statusCounts={bucketStatusCounts[bucket._id]}
                    statusCountsAnyArchive={bucketStatusCountsAnyArchive[bucket._id]}
                    droppedId={droppedId}
                    isExpanded={true}
                    isWorkingBucket={slotIdx === 0}
                    selectedIds={selectedMap[bucket._id]}
                    onToggleSelect={(storyId, meta) => handleToggleSelect(bucket._id, storyId, meta)}
                    onSelectAll={handleSelectAll}
                    onBulkStatusChange={handleBulkStatusChange}
                    searchQuery={searchQueries[bucket._id] || ''}
                    onSearchChange={handleSearchChange}
                    archiveFilter={archiveFilters[bucket._id] || 'hide'}
                    onArchiveFilterChange={handleArchiveFilterChange}
                    onArchive={handleArchive}
                    onUnarchive={handleUnarchive}
                    onShare={handleShare}
                    sharing={sharingBucket === bucket._id}
                    onCopyPublicLink={handleCopyPublicLink}
                    onSendToPublisher={handleSendToPublisher}
                    onOpenAssign={handleOpenAssign}
                    onBulkArchive={handleBulkArchive}
                    onBulkDelete={requestBulkDelete}
                    currentUserId={user?.id}
                    onToggleExpand={() => {
                      if (slotIdx === 0) return; // cannot close working bucket
                      closeBucketSlot(bucket._id);
                    }}
                  />
                </DroppableOpenSlot>
              ))}
            </div>

            {/* Closed buckets — packed masonry beside open slots */}
            {closedBuckets.length > 0 && (
              <div className="bucket-board-closed">
                {closedBuckets.map((bucket) => {
                  const globalIdx = visibleBuckets.indexOf(bucket);
                  return (
                  <DraggableBucketColumn
                    key={bucket._id}
                    bucket={bucket}
                    index={globalIdx}
                    minIndex={openSlots.length}
                    onBucketReorder={handleBucketReorder}
                    onSwapFromOpen={handleSwapToOpenSlot}
                    isExpanded={false}
                  >
                    <BucketColumn
                      bucket={bucket}
                      stories={bucketStories[bucket._id] || []}
                      allBuckets={buckets}
                      onDropFromOther={handleDropFromOther}
                      onReorder={handleReorderAndPersist}
                      onStatusChange={handleStatusChange}
                      onAdvanceStatus={handleAdvanceStatus}
                      onRemove={requestRemove}
                      onCopyTo={handleCopyTo}
                      onMoveTo={handleMoveTo}
                      statusFilter={statusFilters[bucket._id] || null}
                      onStatusFilterChange={handleStatusFilterChange}
                    statusCounts={bucketStatusCounts[bucket._id]}
                    statusCountsAnyArchive={bucketStatusCountsAnyArchive[bucket._id]}
                      droppedId={droppedId}
                      isExpanded={false}
                      isWorkingBucket={false}
                      selectedIds={selectedMap[bucket._id]}
                      onToggleSelect={(storyId, meta) => handleToggleSelect(bucket._id, storyId, meta)}
                      onSelectAll={handleSelectAll}
                      onBulkStatusChange={handleBulkStatusChange}
                      searchQuery={searchQueries[bucket._id] || ''}
                      onSearchChange={handleSearchChange}
                      archiveFilter={archiveFilters[bucket._id] || 'hide'}
                      onArchiveFilterChange={handleArchiveFilterChange}
                      onArchive={handleArchive}
                      onUnarchive={handleUnarchive}
                      onShare={handleShare}
                      sharing={sharingBucket === bucket._id}
                      onCopyPublicLink={handleCopyPublicLink}
                      onSendToPublisher={handleSendToPublisher}
                      onOpenAssign={handleOpenAssign}
                      onBulkArchive={handleBulkArchive}
                      onBulkDelete={requestBulkDelete}
                      currentUserId={user?.id}
                      onToggleExpand={() => openBucketSlot(bucket._id)}
                    />
                  </DraggableBucketColumn>
                  );
                })}
              </div>
            )}
            <div className="bucket-board-hint">Drag between buckets = Move · Shift + Drag = Copy · Drag within bucket = Reorder</div>
          </div>
          );
        })()}
      </div>
      )}

      {activeView === 'inProduction' && (
      <div className="bucket-manager-body" style={{ display: 'block', padding: 20, overflowY: 'auto' }}>
        <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#888' }}>All stories currently in production across all publisher buckets.</p>

        {/* Metrics bar */}
        <div className="in-prod-metrics">
          <div className="in-prod-metric" style={{ borderLeftColor: '#546e7a' }}>
            <div className="in-prod-metric-count">
              {IN_PROD_STATUSES.reduce((sum, s) => sum + (inProdStatusCounts[s] || 0), 0)}
            </div>
            <div className="in-prod-metric-label">Total</div>
          </div>
          {IN_PROD_STATUSES.map(s => (
            <div
              key={s}
              className={`in-prod-metric ${inProdStatusFilter === s ? 'in-prod-metric-active' : ''}`}
              style={{ borderLeftColor: STATUS_COLORS[s], cursor: 'pointer' }}
              onClick={() => handleInProdFilterChange(s)}
            >
              <div className="in-prod-metric-count">{inProdStatusCounts[s] || 0}</div>
              <div className="in-prod-metric-label">{STATUS_LABELS[s]}</div>
            </div>
          ))}
        </div>

        {/* Status filter bar */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            className="bucket-col-btn"
            onClick={() => loadInProduction(1, inProdStatusFilter)}
            title="Refresh"
            style={{ marginRight: 6 }}
          >
            <RefreshOutlinedIcon style={{ fontSize: 14 }} /> Refresh
          </button>
          <button
            className={`bucket-filter-btn ${!inProdStatusFilter ? 'active' : ''}`}
            onClick={() => { setInProdStatusFilter(null); loadInProduction(1, null); }}
          >All</button>
          {IN_PROD_STATUSES.map(s => (
            <button
              key={s}
              className={`bucket-filter-btn ${inProdStatusFilter === s ? 'active' : ''}`}
              style={inProdStatusFilter === s ? { backgroundColor: STATUS_COLORS[s], borderColor: STATUS_COLORS[s], color: '#fff' } : undefined}
              onClick={() => handleInProdFilterChange(s)}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        {loadingInProd ? (
          <div className="bucket-manager-loading">Loading...</div>
        ) : inProdItems.length === 0 ? (
          <div className="bucket-manager-loading">
            No stories in production{inProdStatusFilter ? ` with status "${STATUS_LABELS[inProdStatusFilter]}"` : ''}.
          </div>
        ) : (
          <>
            <div className="in-prod-table-wrapper">
              <table className="in-prod-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Story</th>
                    <th>Bucket</th>
                    <th style={{ width: 60 }}>Articles</th>
                    {showInProdPageViews ? <th style={{ width: 90 }}>Page Views</th> : null}
                    {showInProdFinalUrl ? <th>Final URL</th> : null}
                    <th style={{ width: 100 }}>Status</th>
                    <th style={{ width: 140 }}>Who writes</th>
                    <th style={{ width: 100 }}>Assignee</th>
                    <th style={{ width: 90 }}>Feedback</th>
                    <th style={{ width: 100 }}>Added</th>
                    <th style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody
                  onFocus={(e) => {
                    const tr = e.target.closest && e.target.closest('tr[data-item-id]');
                    if (tr) setFocusedRowId(tr.getAttribute('data-item-id'));
                  }}
                  onBlur={(e) => {
                    // Only clear when focus leaves the tbody entirely.
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    setFocusedRowId(null);
                  }}
                >
                  {inProdItems.map((item, idx) => (
                    <tr
                      key={item._id}
                      data-item-id={item._id}
                      style={item.isArchived ? { opacity: 0.55 } : undefined}
                    >
                      <td style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1565c0', textAlign: 'center' }}>
                        #{(inProdPage - 1) * 50 + idx + 1}
                      </td>
                      <td style={{ maxWidth: 300 }}>
                        {item.story?.slug ? (
                          <a
                            href={`/story/${item.story.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1976d2', textDecoration: 'none' }}
                          >
                            {item.story?.title || 'Unknown story'}
                          </a>
                        ) : (item.story?.title || 'Unknown story')}
                        {item.isArchived && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#999' }}><ArchiveOutlinedIcon style={{ fontSize: 11 }} /> archived</span>}
                      </td>
                      <td style={{ fontWeight: 500, color: '#1565c0', fontSize: '0.85rem' }}>
                        {item.bucket?.name || '—'}
                      </td>
                      <td>{item.articleCountSnapshot || item.story?.articleCount || 0}</td>
                      {showInProdPageViews ? <td>{item.latestPageViews ? item.latestPageViews.toLocaleString() : ''}</td> : null}
                      {showInProdFinalUrl ? (
                        <td>
                          {item.publicationUrl ? (
                            <a href={item.publicationUrl} target="_blank" rel="noopener noreferrer" className="bucket-story-published-link">
                              Open URL
                            </a>
                          ) : ''}
                        </td>
                      ) : null}
                      <td>
                        <span
                          className="in-prod-status-badge"
                          style={{ backgroundColor: STATUS_COLORS[item.status] || '#ccc', cursor: 'pointer' }}
                          onClick={() => handleInProdBadgeCycle(item._id)}
                          title={`Click to change to ${STATUS_LABELS[IN_PROD_FORWARD[item.status]] || 'next'}`}
                        >
                          {STATUS_LABELS[item.status] || item.status}
                        </span>
                        {inProdPending.has(item._id) && (
                          <span
                            title="Updated by another user — will refresh after you finish editing."
                            aria-label="Updated by another user"
                            style={{ marginLeft: 6, color: '#ff9800', fontSize: '0.85rem', cursor: 'help' }}
                          >
                            ↻
                          </span>
                        )}
                      </td>
                      <td>
                        <select
                          style={{ width: '100%', fontSize: '0.75rem', padding: '2px 4px', border: '1px solid #ddd', borderRadius: 4 }}
                          value={item.productionType || 'alchemiq'}
                          onChange={(e) => handleInProdProductionType(item._id, e.target.value)}
                        >
                          <option value="alchemiq">alchemiq</option>
                          <option value="publisher">Publishers</option>
                        </select>
                      </td>
                      <td>
                        <button
                          style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', padding: 0, fontSize: '0.8rem', textDecoration: 'underline' }}
                          onClick={() => handleInProdOpenAssign(item)}
                        >
                          {getUserName(item.assignedAdminUserId)}
                        </button>
                      </td>
                      <td>
                        {item.clientFeedbackStatus ? (
                          <span style={{ color: item.clientFeedbackStatus === 'accepted' ? '#4CAF50' : '#F44336', fontWeight: 600, fontSize: '0.8rem' }}>
                            {item.clientFeedbackStatus === 'accepted'
                              ? <><CheckCircleOutlineIcon style={{ fontSize: 12, verticalAlign: 'middle' }} /> Accepted</>
                              : <><CancelOutlinedIcon style={{ fontSize: 12, verticalAlign: 'middle' }} /> Rejected</>}
                          </span>
                        ) : (
                          <span style={{ color: '#999', fontSize: '0.8rem' }}>—</span>
                        )}
                        {item.clientFeedbackComment && (
                          <span title={item.clientFeedbackComment} style={{ marginLeft: 4, cursor: 'help', fontSize: '0.75rem' }}>
                            <ChatBubbleOutlineIcon style={{ fontSize: 12 }} />
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.8rem' }}>{formatIsraelTime(item.createdAt)}</td>
                      <td>
                        <InProdActionsMenu
                          item={item}
                          onStatusChange={handleInProdStatusChange}
                          onAdvance={handleInProdAdvance}
                          onMove={handleInProdMove}
                          onCopy={handleInProdCopy}
                          onArchive={handleInProdArchive}
                          onRemove={handleInProdRemove}
                          onOpenChange={handleInProdMenuOpenChange}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {inProdTotal > 50 && (
              <div className="in-prod-pagination">
                <button
                  className="bucket-col-btn"
                  disabled={inProdPage <= 1}
                  onClick={() => loadInProduction(inProdPage - 1, inProdStatusFilter)}
                >
                  Previous
                </button>
                <span style={{ fontSize: '0.85rem', color: '#666' }}>
                  Page {inProdPage} of {Math.ceil(inProdTotal / 50)}
                </span>
                <button
                  className="bucket-col-btn"
                  disabled={inProdPage * 50 >= inProdTotal}
                  onClick={() => loadInProduction(inProdPage + 1, inProdStatusFilter)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {activeView === 'published' && (
      <div className="bucket-manager-body" style={{ display: 'block', padding: 20, overflowY: 'auto' }}>
        <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#888' }}>
          Stories that have been published across all publisher buckets.
        </p>

        {loadingPublished ? (
          <div className="bucket-manager-loading">Loading...</div>
        ) : publishedItems.length === 0 ? (
          <div className="bucket-manager-loading">No published stories yet.</div>
        ) : (
          <>
            <div className="in-prod-table-wrapper">
              <table className="in-prod-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Story</th>
                    <th>Bucket</th>
                    <th style={{ width: 60 }}>Articles</th>
                    {showPublishedPageViews ? <th style={{ width: 90 }}>Page Views</th> : null}
                    {showPublishedFinalUrl ? <th>Final URL</th> : null}
                    <th style={{ width: 140 }}>Who wrote</th>
                    <th style={{ width: 90 }}>Feedback</th>
                    <th style={{ width: 120 }}>Published</th>
                  </tr>
                </thead>
                <tbody>
                  {publishedItems.map((item, idx) => (
                    <tr key={item._id}>
                      <td style={{ fontWeight: 600, fontSize: '0.9rem', color: '#4CAF50', textAlign: 'center' }}>
                        #{(publishedPage - 1) * 50 + idx + 1}
                      </td>
                      <td style={{ maxWidth: 300 }}>
                        {item.story?.slug ? (
                          <a
                            href={`/story/${item.story.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1976d2', textDecoration: 'none' }}
                          >
                            {item.story?.title || 'Unknown story'}
                          </a>
                        ) : (item.story?.title || 'Unknown story')}
                      </td>
                      <td style={{ fontWeight: 500, color: '#1565c0', fontSize: '0.85rem' }}>
                        {item.bucket?.name || '—'}
                      </td>
                      <td>{item.articleCountSnapshot || item.story?.articleCount || 0}</td>
                      {showPublishedPageViews ? <td>{item.latestPageViews ? item.latestPageViews.toLocaleString() : ''}</td> : null}
                      {showPublishedFinalUrl ? (
                        <td>
                          {item.publicationUrl ? (
                            <a href={item.publicationUrl} target="_blank" rel="noopener noreferrer" className="bucket-story-published-link">
                              Open URL
                            </a>
                          ) : ''}
                        </td>
                      ) : null}
                      <td style={{ fontSize: '0.8rem' }}>
                        {item.productionType === 'publisher' ? 'Publishers' : 'alchemiq'}
                      </td>
                      <td>
                        {item.clientFeedbackStatus ? (
                          <span style={{ color: item.clientFeedbackStatus === 'accepted' ? '#4CAF50' : '#F44336', fontWeight: 600, fontSize: '0.8rem' }}>
                            {item.clientFeedbackStatus === 'accepted'
                              ? <><CheckCircleOutlineIcon style={{ fontSize: 12, verticalAlign: 'middle' }} /> Accepted</>
                              : <><CancelOutlinedIcon style={{ fontSize: 12, verticalAlign: 'middle' }} /> Rejected</>}
                          </span>
                        ) : (
                          <span style={{ color: '#999', fontSize: '0.8rem' }}>—</span>
                        )}
                        {item.clientFeedbackComment && (
                          <span title={item.clientFeedbackComment} style={{ marginLeft: 4, cursor: 'help', fontSize: '0.75rem' }}>
                            <ChatBubbleOutlineIcon style={{ fontSize: 12, verticalAlign: 'middle' }} />
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.8rem' }}>{formatIsraelTime(item.publishedAt || item.statusChangedAt || item.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {publishedTotal > 50 && (
              <div className="in-prod-pagination">
                <button
                  className="bucket-col-btn"
                  disabled={publishedPage <= 1}
                  onClick={() => loadPublished(publishedPage - 1)}
                >
                  Previous
                </button>
                <span style={{ fontSize: '0.85rem', color: '#666' }}>
                  Page {publishedPage} of {Math.ceil(publishedTotal / 50)}
                </span>
                <button
                  className="bucket-col-btn"
                  disabled={publishedPage * 50 >= publishedTotal}
                  onClick={() => loadPublished(publishedPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {activeView === 'recommended' && (
      <div className="bucket-manager-body" style={{ display: 'block', padding: 20, overflowY: 'auto' }}>
        <div className="rec-toolbar">
          <select
            className="rec-bucket-select"
            value={recBucketId || ''}
            onChange={e => handleRecBucketChange(e.target.value || null)}
          >
            <option value="">Select a bucket...</option>
            {buckets.filter(b => b.isActive).map(b => (
              <option key={b._id} value={b._id}>{b.name}{b.targetLanguage ? ` (${b.targetLanguage})` : ''}</option>
            ))}
          </select>
          {recBucketId && isAdmin && (
            <>
              <button
                className="bucket-col-btn"
                onClick={handleRefreshRecs}
                disabled={refreshingRecs}
              >
                <RefreshOutlinedIcon style={{ fontSize: 14 }} />
                {refreshingRecs ? ' Generating...' : recGenerationKey ? ' Refresh' : ' Generate'}
              </button>
              {recSelected.size > 0 && (
                <button
                  className="bucket-col-btn rec-add-selected-btn"
                  onClick={handleAddSelectedRecs}
                  disabled={recBulkAdding}
                >
                  <AddOutlinedIcon style={{ fontSize: 14 }} />
                  {recBulkAdding ? ' Adding...' : ` Add ${recSelected.size} selected`}
                </button>
              )}
            </>
          )}
        </div>

        {!recBucketId ? (
          <div className="bucket-manager-loading">Select a bucket to view recommendations.</div>
        ) : loadingRecs ? (
          <div className="bucket-manager-loading">Loading recommendations...</div>
        ) : recommendations.length === 0 ? (
          <div className="rec-empty-state">
            <p>No recommendations yet for this bucket.</p>
            {isAdmin && (
              <button className="bucket-col-btn" onClick={handleRefreshRecs} disabled={refreshingRecs}>
                <AutoAwesomeOutlinedIcon style={{ fontSize: 14 }} />
                {refreshingRecs ? ' Generating...' : ' Generate Recommendations'}
              </button>
            )}
          </div>
        ) : (
          <div className="in-prod-table-wrapper">
            <table className="in-prod-table rec-table">
              <thead>
                <tr>
                  {isAdmin && (
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={recommendations.length > 0 && recSelected.size === recommendations.filter(r => !r.addedToBucketAt).length && recommendations.filter(r => !r.addedToBucketAt).length > 0}
                      onChange={() => {
                        const addable = recommendations.filter(r => !r.addedToBucketAt).map(r => r._id);
                        if (recSelected.size === addable.length) {
                          setRecSelected(new Set());
                        } else {
                          setRecSelected(new Set(addable));
                        }
                      }}
                    />
                  </th>
                  )}
                  <th style={{ width: 40 }}>#</th>
                  <th>Story</th>
                  <th style={{ width: 60 }}>Score</th>
                  <th style={{ width: 220 }}>Reason</th>
                  <th style={{ width: 120 }}>Matched</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.map(rec => {
                  const recId = rec._id;
                  const story = rec.storyId && typeof rec.storyId === 'object' ? rec.storyId : null;
                  const title = story?.title || rec.title || 'Unknown story';
                  const isAdded = !!rec.addedToBucketAt;
                  const matched = rec.matchedAttributes || {};
                  const cats = (matched.categories || []).map(e => e.key || e).slice(0, 3);
                  const kws = (matched.keywords || []).map(e => e.key || e).slice(0, 3);
                  return (
                    <tr key={recId} className={isAdded ? 'rec-row-added' : ''}>
                      {isAdmin && (
                      <td>
                        {!isAdded && (
                          <input
                            type="checkbox"
                            checked={recSelected.has(recId)}
                            onChange={() => setRecSelected(prev => {
                              const next = new Set(prev);
                              next.has(recId) ? next.delete(recId) : next.add(recId);
                              return next;
                            })}
                          />
                        )}
                      </td>
                      )}
                      <td style={{ fontWeight: 600, color: '#2196F3', textAlign: 'center' }}>
                        {rec.rank}
                      </td>
                      <td style={{ maxWidth: 300 }}>
                        {story?.slug ? (
                          <a href={`/story/${story.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                            {title}
                          </a>
                        ) : title}
                        {story?.articleCount > 0 && (
                          <span className="rec-article-count">{story.articleCount} articles</span>
                        )}
                      </td>
                      <td>
                        <span className="rec-score-pill">{rec.candidateScore}</span>
                      </td>
                      <td className="rec-explanation">{rec.explanation || ''}</td>
                      <td className="rec-matched">
                        {cats.map(c => <span key={c} className="rec-tag">{c}</span>)}
                        {kws.map(k => <span key={k} className="rec-tag kw">{k}</span>)}
                      </td>
                      <td>
                        {isAdded ? (
                          <span className="rec-added-badge">Added</span>
                        ) : (
                          <div className="rec-actions">
                            {isAdmin && (
                              <button
                                className="bucket-col-btn rec-action-btn"
                                onClick={() => handleAddOneRec(recId)}
                                disabled={recAdding === recId}
                                title="Add to bucket"
                              >
                                {recAdding === recId ? '...' : 'Add'}
                              </button>
                            )}
                            <button
                              className="bucket-col-btn rec-action-btn dismiss"
                              onClick={() => handleDismissRec(recId)}
                              title="Dismiss"
                            >
                              <CloseIcon style={{ fontSize: 14 }} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Move confirmation modal */}
      <MoveModal
        show={!!moveModal}
        item={moveModal?.item}
        sourceBucket={moveModal?.sourceBucketName}
        destBucket={moveModal?.destBucketName}
        onConfirm={confirmMove}
        onCancel={() => setMoveModal(null)}
        inProdMode={moveModal?.inProdMode}
        copyMode={moveModal?.copyMode}
        allBuckets={buckets}
        onSetDestBucket={(destId) => setMoveModal(prev => prev ? { ...prev, destBucketId: destId, destBucketName: buckets.find(b => b._id === destId)?.name || '' } : null)}
      />

      {/* Remove confirmation modal */}
      <RemoveModal
        show={!!removeModal}
        item={removeModal?.item}
        bucketName={removeModal?.bucketName}
        onConfirm={confirmRemove}
        onCancel={() => setRemoveModal(null)}
      />

      {/* Bulk delete confirmation modal */}
      <AppModal
        isOpen={!!bulkDeleteModal}
        title="Remove Stories"
        onCancel={() => setBulkDeleteModal(null)}
        onConfirm={confirmBulkDelete}
        confirmLabel={`Remove ${bulkDeleteModal?.ids?.length || 0}`}
        confirmVariant="danger"
      >
        <p>
          Remove <strong>{bulkDeleteModal?.ids?.length || 0}</strong> selected stories from <strong>{bulkDeleteModal?.bucketName}</strong>?
        </p>
        <p style={{ color: '#888', fontStyle: 'italic', fontSize: '13px' }}>
          This will remove the stories from the bucket entirely. It won't delete the stories themselves.
        </p>
      </AppModal>

      {/* Send link. The SHARED confirmation, mounted here, on BucketStories
          and on BucketSidebar. One component so a channel added later cannot
          reach one page and miss the other two. */}
      <SendSuggestionsModal
        bucketId={sendModalBucket}
        isOpen={Boolean(sendModalBucket)}
        onClose={() => setSendModalBucket(null)}
        onResult={(message, type) => setToast({ message, type })}
        onSent={() => loadBucketStories(sendModalBucket)}
      />


      {/* Assignment modal */}
      {assignModal && (() => {
        const q = assignSearch.toLowerCase();
        const filtered = assignableUsers.filter(u =>
          !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
        );
        const meIdx = filtered.findIndex(u => (u.fronteggUserId || u._id) === user?.id);
        const sorted = meIdx > 0
          ? [filtered[meIdx], ...filtered.slice(0, meIdx), ...filtered.slice(meIdx + 1)]
          : filtered;
        // No onConfirm: the row click below IS the assignment, and AppModal
        // renders Cancel on its own when no confirm handler is given.
        return (
          <AppModal
            isOpen={Boolean(assignModal)}
            title="Assign User"
            onCancel={() => setAssignModal(null)}
            maxWidth={420}
          >
            <p style={{ fontSize: 13, color: '#666' }}>{assignModal.storyTitle}</p>
            <p style={{ fontSize: 12, color: '#3C4A5E', margin: '0 0 8px' }}>
              {assignSaving ? 'Assigning…' : 'Click a name to assign it.'}
            </p>
            <input
              type="text"
              placeholder="Search users…"
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }}
            />
            <div style={{ maxHeight: 250, overflowY: 'auto', border: '1px solid #e1e4e8', borderRadius: 6, background: '#fafbfc' }}>
              <div
                role="button"
                tabIndex={0}
                style={{
                  padding: '8px 12px',
                  cursor: assignSaving ? 'wait' : 'pointer',
                  background: !selectedAssigneeId ? '#e3f2fd' : 'transparent',
                  borderBottom: '1px solid #f0f0f0',
                  opacity: assignSaving ? 0.6 : 1,
                }}
                onClick={() => handleAssignPick('')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAssignPick(''); } }}
              >
                <em style={{ color: '#999' }}>Unassigned</em>
              </div>
              {sorted.map(u => {
                const uid = u.fronteggUserId || u._id;
                const isMe = uid === user?.id;
                return (
                  <div
                    key={uid}
                    role="button"
                    tabIndex={0}
                    style={{
                      padding: '8px 12px',
                      cursor: assignSaving ? 'wait' : 'pointer',
                      background: selectedAssigneeId === uid ? '#e3f2fd' : 'transparent',
                      borderBottom: '1px solid #f0f0f0',
                      opacity: assignSaving ? 0.6 : 1,
                    }}
                    onClick={() => handleAssignPick(uid)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAssignPick(uid); } }}
                  >
                    <strong>{u.name || 'No name'}</strong>{isMe && <span style={{ marginLeft: 6, color: '#2196F3', fontSize: '0.75rem' }}>(you)</span>}
                    <span style={{ marginLeft: 8, color: '#999', fontSize: '0.8rem' }}>{u.email}</span>
                  </div>
                );
              })}
              {sorted.length === 0 && <div style={{ padding: 12, color: '#999', textAlign: 'center' }}>No eligible users found</div>}
            </div>
          </AppModal>
        );
      })()}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default BucketManager;
